import { clientLogger } from '@/lib/clientLogger';
import { useState, useRef, useEffect, useCallback, useMemo, lazy, Suspense, type RefObject } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import { JobDTO, JobMergePatchDTO, PaymentStatus, JobStatusDTO, Technician, JobItemOperationDTO, CompanyInvoiceDTO, JobTypeBasicDTO, TagDTO, InvoiceDto, ConvertJobToEstimateResultDto } from '@/types';
import CreateEstimateFromJobDialog from './CreateEstimateFromJobDialog';
import LinkedEstimates from './LinkedEstimates';
import { X, Calendar, User, FileText, CreditCard, Clock, Trash2, Truck, MessageSquare, Phone, Paperclip, MoreVertical, ChevronDown, ChevronRight, Edit2, DollarSign, CalendarPlus, Link, Check, MapPin, Receipt, Share, QrCode, Zap } from 'lucide-react';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import { useExtensibleFields, formatFieldValue, getFieldValue, mergeExtraFields } from '@/hooks/useExtensibleFields';
import {
    formatDateTimeRangeInCompanyTimeZone,
    formatInstantInCompanyTimeZone,
    formatDateOnlyInCompanyTimeZone,
    normalizeBackendUtcIsoString,
    isScheduleRange,
} from '@/lib/dateUtils';
import { calculateTotal } from '@/lib/invoiceCalculations';
import { ConfirmDialog } from './ui/ConfirmDialog';
import { destructiveCopy } from '@/lib/destructiveConfirmMessages';
import { useGoogleMapsAutocomplete, getAddressWithZipCode } from '@/hooks/useGoogleMapsAutocomplete';
import { MapWidget } from './MapWidget';
import { JobItemsSection } from './ui/JobItemsSection';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useVisualKeyboardOpen } from '@/hooks/useVisualKeyboardOpen';
import { useNotifications, notificationService } from './ui/NotificationSystem';
import { copyToClipboard, openNavigation } from '@/lib/capacitor';
import { useBackNavigationLayer } from '@/lib/navigation';
import type { ModalLayer } from '@/lib/layerManager';
import { formatPhoneNumber } from '@/lib/phoneUtils';

import { createPortal } from 'react-dom';
import { JobAttachmentsModal } from './JobAttachmentsModal';
import { JobExpenseAttachmentsModal } from './JobExpenseAttachmentsModal';
import { JobContactsMobileModal } from './JobContactsMobileModal';
import { JobDetailsModal } from './JobDetailsModal';
import { PaymentCard } from './PaymentCard';
import { SmsMethodSelector } from './SmsMethodSelector';
import { SmsTemplateMenu } from './SmsTemplateMenu';
import { SendSmsModal } from './SendSmsModal';
import { JobShareModal } from './JobShareModal';
import { JobReviewQrModal } from './JobReviewQrModal';
import { CallMethodSheet } from './CallMethodSheet';
import { JobExpensesCard } from './JobExpensesCard';
import { TechnicianPaySection } from './TechnicianPaySection';
import { InvoiceDetailsModal } from './InvoiceDetailsModal';
import { TagSelector } from './ui/TagSelector';
import { gentleDateTimePickerRootSx, gentleDateTimePickerSlotProps, usePickerValue } from './ui/gentleDateTimePicker';
import { CollapsibleSummaryHeader } from './ui/CollapsibleSummaryHeader';
import { TakePaymentButton } from './ui/TakePaymentButton';
import { useJobEditPermissions } from '@/hooks/useJobEditPermissions';
import { useCompletedJobEditLock } from '@/hooks/useCompletedJobEditLock';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { usePermissions } from '@/hooks/usePermissions';
import { NewJobModal } from './NewJobModal';
import { useCompanyTimezone } from '@/hooks/useGlobalCompany';
import { ContactDTO } from '@/types';
import { JobVisitsModal } from './JobVisitsModal';
import { buildTechnicianSelectOptions, hasInactiveTechnicians } from '@/lib/technicianSelectors';
import { CustomSelect } from './ui/CustomSelect';
import { SubscriptionBanner } from './SubscriptionBanner';
import { useVoiceCall } from '@/hooks/useVoiceCall';
import { layoutViewportHeight } from '@/lib/viewport';
import { AsapBadge } from '@/components/jobs/AsapBadge';
import { CallMaskingOptions } from './CallMaskingOptions';

const VoipCallHistoryPage = lazy(() => import('./VoipCallHistoryPage').then(m => ({ default: m.VoipCallHistoryPage })));

/** Approximate height of the two-row phone picker; used to choose open up vs down (see JobsPage dropdowns). */
const CONTACT_PHONE_PICKER_MENU_HEIGHT_PX = 120;

function shouldOpenContactPhoneMenuUpward(anchorEl: HTMLElement): boolean {
    const rect = anchorEl.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    return spaceBelow < CONTACT_PHONE_PICKER_MENU_HEIGHT_PX && rect.top > CONTACT_PHONE_PICKER_MENU_HEIGHT_PX;
}

interface JobDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    job: JobDTO | null;
    onDelete?: (job: JobDTO) => void;
    onCancelJob?: (job: JobDTO, reason?: string) => void;
    onUncancelJob?: (job: JobDTO) => void;
    onJobUpdated?: (updatedJob: JobDTO) => void;
    openedFromInvoice?: boolean;  // Opened from InvoiceDetailsModal — Invoice button closes instead of re-opening
    openedFromPastJobs?: boolean;
    layer?: ModalLayer;
}


/** Grow a text box to show all its lines (no inner scrolling). */
function fitTextareaToText(el: HTMLTextAreaElement) {
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
}

export function JobDetailsMobile({ isOpen, onClose, job, onDelete, onCancelJob, onUncancelJob, onJobUpdated, openedFromInvoice, layer }: JobDetailsModalProps) {
    const isMobile = useIsMobile();
    const isKeyboardOpen = useVisualKeyboardOpen(isOpen);
    const { addNotification } = useNotifications();
    const { openEstimate } = useVoiceCall();
    const { canEditJobs, canEditField: canEditFieldRaw, canEditCompletedJobs, canViewPayoutFields } = useJobEditPermissions();
    const { userRole, isAdmin } = useCurrentUser();
    const { isFeatureAllowed } = usePermissions();
    const isTechnician = userRole === 'technician';
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [showConfirmCancel, setShowConfirmCancel] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [showConfirmUncancel, setShowConfirmUncancel] = useState(false);
    const [showConfirmMarkAsap, setShowConfirmMarkAsap] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [startPickerOpen, setStartPickerOpen] = useState(false);
    const [endPickerOpen, setEndPickerOpen] = useState(false);
    const [scheduleExpanded, setScheduleExpanded] = useState(false);

    useBackNavigationLayer({
        enabled: startPickerOpen || endPickerOpen,
        onBack: () => {
            setStartPickerOpen(false);
            setEndPickerOpen(false);
        }
    });

    // Per-card editing state (for inline editing)
    type EditableCard = 'financials' | 'notes' | 'description' | 'internal_notes' | null;
    const [editingCard, setEditingCard] = useState<EditableCard>(null);
    const [showContactsModal, setShowContactsModal] = useState(false);
    const [pastJobId, setPastJobId] = useState<string | null>(null);
    const [showInactiveTechnicians, setShowInactiveTechnicians] = useState(false);
    const [showContactPhoneCallMenu, setShowContactPhoneCallMenu] = useState(false);
    const [showContactPhoneSmsMenu, setShowContactPhoneSmsMenu] = useState(false);

    const [callSheetState, setCallSheetState] = useState<{
        isOpen: boolean;
        toE164: string;
        displayName: string;
        jobId?: string;
        masked?: boolean;
        contactId?: string;
        contactWhich?: 'primary' | 'additional';
    }>({ isOpen: false, toE164: '', displayName: '' });
    const [showPaymentStatusDropdown, setShowPaymentStatusDropdown] = useState(false);
    const [showJobTypeDropdown, setShowJobTypeDropdown] = useState(false);
    const jobTypeDropdownRef = useRef<HTMLDivElement>(null);
    const contactPhoneActionsRef = useRef<HTMLDivElement>(null);
    const contactCallMenuButtonRef = useRef<HTMLButtonElement>(null);
    const contactSmsMenuButtonRef = useRef<HTMLButtonElement>(null);
    const [contactCallMenuOpenUp, setContactCallMenuOpenUp] = useState(false);
    const [contactSmsMenuOpenUp, setContactSmsMenuOpenUp] = useState(false);

    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [showImagesModal, setShowImagesModal] = useState(false);
    const [showExpenseAttachmentsModal, setShowExpenseAttachmentsModal] = useState(false);

    const [showMetaInfo, setShowMetaInfo] = useState(false);
    const [showPaymentsExpanded, setShowPaymentsExpanded] = useState(false);
    const [showExpensesExpanded, setShowExpensesExpanded] = useState(false);
    const [isAdditionalFieldsExpanded, setIsAdditionalFieldsExpanded] = useState(true);
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const statusDropdownRef = useRef<HTMLDivElement>(null);

    // More menu state
    const [showMoreMenu, setShowMoreMenu] = useState(false);
    const [showReviewQr, setShowReviewQr] = useState(false);
    const moreMenuRef = useRef<HTMLDivElement>(null);
    const [showConvertConfirm, setShowConvertConfirm] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [isScheduleVisitOpen, setIsScheduleVisitOpen] = useState(false);
    const [viewOriginJobId, setViewOriginJobId] = useState<string | null>(null);
    const [viewFollowUpJobId, setViewFollowUpJobId] = useState<string | null>(null);
    const [showItemEditSignatureWarning, setShowItemEditSignatureWarning] = useState(false);
    const [pendingItemOperations, setPendingItemOperations] = useState<JobItemOperationDTO[] | null>(null);
    const pendingItemPromiseRef = useRef<{ resolve: () => void; reject: (e: Error) => void } | null>(null);

    const [showVisitsModal, setShowVisitsModal] = useState(false);
    const [showCallsModal, setShowCallsModal] = useState(false);

    // SMS selector state
    const [smsSelectorState, setSmsSelectorState] = useState<{
        isOpen: boolean;
        recipientPhone: string;
        recipientName: string;
        recipientType: 'Technician' | 'Client';
        jobId?: string;
        masked?: boolean;
        contactId?: string;
        contactWhich?: 'primary' | 'additional';
        technicianUserId?: string;
    }>({ isOpen: false, recipientPhone: '', recipientName: '', recipientType: 'Client' });
    // "SMS Client" swaps the More menu's rows for the SMS template list; picking one prefills a
    // locked SendSmsModal, "Custom message" falls through to the method selector as before.
    const [showSmsSubmenu, setShowSmsSubmenu] = useState(false);
    const [templateSmsState, setTemplateSmsState] = useState<{
        message: string;
        warning?: string;
        recipientPhone: string;
        recipientName: string;
        toContactRef?: string;
    } | null>(null);
    const primaryContact = job?.contacts?.[0]?.contact;


    // Extra field editing state
    const [editingExtraField, setEditingExtraField] = useState<string | null>(null);
    const [extraFieldInputValue, setExtraFieldInputValue] = useState<string>('');

    // Card refs for click-outside detection
    const financialsCardRef = useRef<HTMLDivElement>(null);
    const notesCardRef = useRef<HTMLDivElement>(null);
    const descriptionCardRef = useRef<HTMLDivElement>(null);


    const cardRefs = useMemo<Record<Exclude<EditableCard, null>, RefObject<HTMLDivElement>>>(() => ({
        financials: financialsCardRef,
        notes: notesCardRef,
        description: descriptionCardRef,
        internal_notes: notesCardRef,
    }), []);

    // Dedicated address editing state (separate from card editing for simpler autocomplete handling)
    const [isEditingAddress, setIsEditingAddress] = useState(false);
    const [addressValue, setAddressValue] = useState('');
    const [unitNameValue, setUnitNameValue] = useState('');
    const [hasTypedInAddress, setHasTypedInAddress] = useState(false);


    // Form state
    const [formData, setFormData] = useState<JobMergePatchDTO>({});
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [extraFields, setExtraFields] = useState<Record<string, any>>({});
    const [scheduleStart, setScheduleStart] = useState<string>('');
    const [scheduleEnd, setScheduleEnd] = useState<string>('');
    const scheduleStartPickerValue = usePickerValue(scheduleStart ? normalizeBackendUtcIsoString(scheduleStart) : null);
    const scheduleEndPickerValue = usePickerValue(scheduleEnd ? normalizeBackendUtcIsoString(scheduleEnd) : null);


    const queryClient = useQueryClient();
    const addressInputRef = useRef<HTMLInputElement>(null);
    const addressSectionRef = useRef<HTMLDivElement>(null);
    const unitNameInputRef = useRef<HTMLInputElement>(null);
    const notesRef = useRef<HTMLTextAreaElement>(null);
    const formRef = useRef<HTMLFormElement>(null);
    const [showFullNotes, setShowFullNotes] = useState(false);

    // Get extensible field configurations for jobs
    // Use company-specific fields if the job has a company, otherwise use global fields
    // Must call hooks before early return to maintain hook order
    const { fieldConfigs } = useExtensibleFields('jobs', job?.company?.id);
    const jobExtensibleFields = fieldConfigs || [];

    const mergedExtraFields = mergeExtraFields(job?.extra_fields, jobExtensibleFields);

    // Fetch job statuses for the company
    const { data: jobStatuses } = useQuery({
        queryKey: ['jobStatuses', job?.company?.id],
        queryFn: () => job?.company?.id ? apiService.getJobStatusesByCompany(job.company.id) : Promise.resolve([]),
        enabled: !!job?.company?.id,
    });

    const { data: jobAttachmentsList } = useQuery({
        queryKey: ['jobAttachments', job?.id],
        queryFn: () => apiService.getJobAttachments(job!.id),
        enabled: isOpen && !!job?.id,
    });
    const attachmentCount = jobAttachmentsList?.length ?? 0;

    const { data: jobExpenseAttachmentsList } = useQuery({
        queryKey: ['jobExpenseAttachments', job?.id],
        queryFn: () => apiService.getJobExpenseAttachments(job!.id),
        enabled: isOpen && !!job?.id,
    });
    const expenseAttachmentCount = jobExpenseAttachmentsList?.length ?? 0;

    const { isCompletedJobLocked } = useCompletedJobEditLock(job, canEditCompletedJobs, isOpen);

    // Wrap canEditField to block editing on completed jobs
    const canEditField = useCallback((field: string): boolean => {
        if (isCompletedJobLocked) return false;
        return canEditFieldRaw(field);
    }, [isCompletedJobLocked, canEditFieldRaw]);

    // Placeholder for NewJobMobile target confirmation - using separate tool call for second file to avoid confusion but multi_replace can handle one file.
    // I will target NewJobMobile in the same tool call if possible, but TargetFile is singular.
    // Ah, multi_replace_file_content takes singular TargetFile.
    // So I must make two calls.
    const { data: technicians, isLoading: techniciansLoading } = useQuery({
        queryKey: ['technicians', job?.company?.id],
        queryFn: () => job?.company?.id ? apiService.getTechnicians(job.company.id) : Promise.resolve([]),
        enabled: !!job?.company?.id && !isTechnician,
    });

    useEffect(() => {
        setShowInactiveTechnicians(false);
    }, [job?.company?.id]);

    // Fetch job types for the company
    const { data: jobTypes, isLoading: jobTypesLoading } = useQuery({
        queryKey: ['jobTypes', job?.company?.id],
        queryFn: () => job?.company?.id ? apiService.getJobTypesByCompany() : Promise.resolve([]),
        enabled: !!job?.company?.id,
    });

    // Fetch assigned technician details to ensure we have phone number
    const { data: assignedTechnicianDetails } = useQuery({
        queryKey: ['technician', job?.assigned_technician?.id],
        queryFn: () => job?.assigned_technician?.id ? apiService.getTechnicianById(job.assigned_technician.id) : Promise.resolve(null),
        enabled: !!job?.assigned_technician?.id && !isTechnician,
    });

    // Fetch company invoice settings to get tax rate
    const { data: companyInvoiceSettings } = useQuery<CompanyInvoiceDTO | null>({
        queryKey: ['company-invoice-settings', job?.company?.id],
        // Must pass the job's company explicitly: with no argument the request interceptor
        // falls back to the globally selected company, so a job in another company would be
        // cached under this key with the wrong company's settings (and review link).
        queryFn: () => job?.company?.id ? apiService.getCompanyInvoiceSettings(job.company.id) : Promise.resolve(null),
        enabled: isOpen && !!job?.company?.id,
        staleTime: 24 * 60 * 60 * 1000, // review link / tax rate / branding barely change
    });
    const companyTimeZone = useCompanyTimezone()?.trim();

    // Initialize form data when entering edit mode
    useEffect(() => {
        if (job && isEditMode) {
            setFormData({
                date_executed_start: job.date_executed_start,
                date_executed_end: job.date_executed_end,
                job_type_id: job.job_type?.id || '',
                address: job.address || '',
                assigned_technician_id: job.assigned_technician?.id || '',
                notes: job.notes || '',
                invoice_number: job.invoice_number || '',
                estimate: job.estimate || '',
                total: job.total || 0,
                source_id: '',
                payment_status: job.payment_status || ('NotPaid' as PaymentStatus),
                extra_fields: job.extra_fields || {},
            });

            setExtraFields(job.extra_fields || {});
        }
    }, [job, isEditMode]);

    // Update job mutation - uses merge-patch to only send changed fields
    const updateJobMutation = useMutation({
        mutationFn: (data: JobMergePatchDTO) => apiService.mergePatchJob(job!.id, data),
        onSuccess: (updatedJob: JobDTO) => {
            queryClient.setQueriesData({ queryKey: ['jobs'] }, (existing: any) => {
                if (!existing) return existing;
                const isArray = Array.isArray(existing);
                const items = isArray ? existing : existing.jobs;
                if (!Array.isArray(items)) return existing;
                const updatedItems = items.map((j: JobDTO) => (j.id === updatedJob.id ? updatedJob : j));
                if (isArray) return updatedItems;
                return { ...existing, jobs: updatedItems };
            });
            queryClient.setQueryData(['job', updatedJob.id], updatedJob);
            setIsEditMode(false);
            if (onJobUpdated) {
                onJobUpdated(updatedJob);
            }
        },
        onError: (error: any) => {
            clientLogger.error('Error updating job:', error);
            setErrors({ submit: 'Failed to update job. Please try again.' });
        },
    });

    // Update job status mutation
    const updateJobStatusMutation = useMutation({
        mutationFn: (statusId: string) => apiService.updateJobEntityStatus(job!.id, { status_id: statusId }),
        onSuccess: (newStatus: JobStatusDTO) => {
            queryClient.invalidateQueries({ queryKey: ['jobs'] });
            queryClient.invalidateQueries({ queryKey: ['search-lookup'] });
            const updatedJob = {
                ...job,
                status: newStatus.name,
                status_id: newStatus.id,
                status_color: newStatus.color
            } as JobDTO;
            queryClient.setQueryData(['job', updatedJob.id], updatedJob);
            // A status change can have server-side effects the local merge above can't know about
            // (e.g. completing an ASAP job replaces its date and clears the flag), so refetch.
            queryClient.invalidateQueries({ queryKey: ['job', updatedJob.id] });
            if (onJobUpdated) {
                onJobUpdated(updatedJob);
            }
        },
        onError: (error: any) => {
            clientLogger.error('Error updating job status:', error);
        },
    });

    // Update job tags mutation
    const setJobTagsMutation = useMutation({
        mutationFn: async (tags: TagDTO[]) => {
            if (!job) return;
            const tagInputs = tags.map(tag => ({ tag_id: tag.id }));
            return apiService.setJobTags(job.id, tagInputs);
        },
        onSuccess: (updatedTags) => {
            const updatedJob = { ...job!, tags: updatedTags };
            queryClient.setQueriesData({ queryKey: ['jobs'] }, (existing: any) => {
                if (!existing) return existing;
                const isArray = Array.isArray(existing);
                const items = isArray ? existing : existing.jobs;
                if (!Array.isArray(items)) return existing;
                const updatedItems = items.map((j: JobDTO) => (j.id === updatedJob.id ? updatedJob : j));
                if (isArray) return updatedItems;
                return { ...existing, jobs: updatedItems };
            });
            queryClient.setQueryData(['job', updatedJob.id], updatedJob);
            if (onJobUpdated) {
                onJobUpdated(updatedJob);
            }
        },
        onError: (error: any) => {
            clientLogger.error('Error updating job tags:', error);
        },
    });

    // Initialize address value when job changes
    useEffect(() => {
        if (job) {
            setAddressValue(job.address || '');
            setUnitNameValue(job.unit_name || '');
            setScheduleStart(normalizeBackendUtcIsoString(job.date_executed_start));
            setScheduleEnd(normalizeBackendUtcIsoString(job.date_executed_end));
        }
    }, [job?.address, job?.unit_name, job?.date_executed_start, job?.date_executed_end]);

    // Monitor the address input field for manual typing (not autocomplete selections)
    // The autocomplete handler uses setAddressKey to force re-render, so we only need to handle manual input
    useEffect(() => {
        const inputElement = addressInputRef.current;
        if (!inputElement) return;

        const handleInput = (e: Event) => {
            const target = e.target as HTMLInputElement;
            // Only sync if value differs - this handles manual typing
            if (target.value !== addressValue) {
                setAddressValue(target.value);
            }
            // Show autocomplete dropdown after user starts typing
            if (!hasTypedInAddress) {
                setHasTypedInAddress(true);
                // Show pac-container now that user has typed
                document.querySelectorAll('.pac-container').forEach((el) => {
                    (el as HTMLElement).style.display = '';
                    (el as HTMLElement).style.visibility = '';
                });
            }
        };

        inputElement.addEventListener('input', handleInput);

        return () => {
            inputElement.removeEventListener('input', handleInput);
        };
    }, [addressValue, hasTypedInAddress]);

    // Hide autocomplete dropdown when entering or exiting edit mode
    useEffect(() => {
        // Hide all Google Maps autocomplete dropdowns
        document.querySelectorAll('.pac-container').forEach((el) => {
            (el as HTMLElement).style.display = 'none';
            (el as HTMLElement).style.visibility = 'hidden';
        });

        if (!isEditingAddress) {
            // Blur input to ensure autocomplete is dismissed
            addressInputRef.current?.blur();
            // Reset typed state when exiting
            setHasTypedInAddress(false);
        }
    }, [isEditingAddress]);

    // Save address and close editing
    const saveAddressAndClose = useCallback(async () => {
        if (!job) return;

        // Hide autocomplete dropdown before closing
        document.querySelectorAll('.pac-container').forEach((el) => {
            (el as HTMLElement).style.display = 'none';
            (el as HTMLElement).style.visibility = 'hidden';
        });
        addressInputRef.current?.blur();

        setIsEditingAddress(false);

        // Get the current input value to ensure we save the correct address
        const currentAddressValue = addressInputRef.current?.value || addressValue;

        // Only save if address or unit changed
        if (currentAddressValue !== job.address || unitNameValue !== job.unit_name) {
            try {
                await updateJobMutation.mutateAsync({
                    address: currentAddressValue,
                    unit_name: unitNameValue || undefined
                });
            } catch (error) {
                clientLogger.error('Failed to save address:', error);
            }
        }
    }, [job, addressValue, unitNameValue, updateJobMutation]);

    // Check if any edit mode is currently active
    const isAnyEditActive = useCallback(() => {
        return editingCard !== null ||
            isEditingAddress ||
            showPaymentStatusDropdown ||
            showStatusDropdown ||
            showJobTypeDropdown ||
            showContactPhoneCallMenu ||
            showContactPhoneSmsMenu;
    }, [editingCard, isEditingAddress, showPaymentStatusDropdown, showStatusDropdown, showJobTypeDropdown, showContactPhoneCallMenu, showContactPhoneSmsMenu]);

    // Cancel all edit modes without saving (reset values)
    const cancelAllEdits = useCallback(() => {
        // Reset address to original value
        if (isEditingAddress && job) {
            setAddressValue(job.address || '');
            setUnitNameValue(job.unit_name || '');
        }
        setIsEditingAddress(false);

        // Close dropdowns
        setShowPaymentStatusDropdown(false);
        setShowStatusDropdown(false);
        setShowJobTypeDropdown(false);
        setShowContactPhoneCallMenu(false);
        setShowContactPhoneSmsMenu(false);

        // Close card editing (reset form data)
        if (editingCard && job) {
            setFormData({});
        }
        setEditingCard(null);

        // Hide autocomplete dropdown
        document.querySelectorAll('.pac-container').forEach((el) => {
            (el as HTMLElement).style.display = 'none';
            (el as HTMLElement).style.visibility = 'hidden';
        });
    }, [editingCard, isEditingAddress, job]);

    const handleInputChange = useCallback((field: string, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));

        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    }, [errors]);

    // Memoized handler for Google Maps place selection (matches JobDetailsDesktop pattern)
    const handlePlaceSelect = useCallback((place: google.maps.places.PlaceResult) => {
        const addressWithZip = getAddressWithZipCode(place);
        if (addressWithZip) {
            setAddressValue(addressWithZip);
            handleInputChange('address', addressWithZip);
            // After selecting an address, move focus to unit name input
            setTimeout(() => unitNameInputRef.current?.focus(), 100);
        }
    }, [handleInputChange]);

    // Google Maps autocomplete for address
    // Keep autocomplete active while modal is open (don't toggle based on isEditingAddress to avoid reinitializing)
    const isAutocompleteActive = isOpen && !!job;

    useGoogleMapsAutocomplete({
        inputRef: addressInputRef,
        onPlaceSelect: handlePlaceSelect,
        isActive: isAutocompleteActive,
    });

    // Build only changed data for merge-patch API (sends only modified fields)
    const buildChangedData = useCallback((): JobMergePatchDTO => {
        if (!job) return {};

        const changes: JobMergePatchDTO = {};

        // Only include fields that have been explicitly set in formData and differ from current job
        if (formData.date_executed_start !== undefined && formData.date_executed_start !== job.date_executed_start) {
            changes.date_executed_start = formData.date_executed_start;
        }
        if (formData.date_executed_end !== undefined && formData.date_executed_end !== job.date_executed_end) {
            changes.date_executed_end = formData.date_executed_end;
        }
        if (formData.job_type_id !== undefined && formData.job_type_id !== job.job_type?.id) {
            changes.job_type_id = formData.job_type_id;
        }
        if (formData.address !== undefined && formData.address !== job.address) {
            changes.address = formData.address;
        }
        if (formData.assigned_technician_id !== undefined && formData.assigned_technician_id !== job.assigned_technician?.id) {
            changes.assigned_technician_id = formData.assigned_technician_id;
        }
        if (formData.notes !== undefined && formData.notes !== job.notes) {
            changes.notes = formData.notes;
        }
        if (formData.description !== undefined && formData.description !== job.description) {
            changes.description = formData.description;
        }
        if (formData.internal_notes !== undefined && formData.internal_notes !== job.internal_notes) {
            changes.internal_notes = formData.internal_notes;
        }
        if (formData.invoice_number !== undefined && formData.invoice_number !== job.invoice_number) {
            changes.invoice_number = formData.invoice_number;
        }
        if (formData.estimate !== undefined && formData.estimate !== job.estimate) {
            changes.estimate = formData.estimate;
        }
        if (formData.total !== undefined && formData.total !== job.total) {
            changes.total = formData.total;
        }
        if (formData.payment_status !== undefined && formData.payment_status !== job.payment_status) {
            changes.payment_status = formData.payment_status;
        }
        if (formData.extra_fields !== undefined && JSON.stringify(formData.extra_fields) !== JSON.stringify(job.extra_fields)) {
            changes.extra_fields = formData.extra_fields;
        }

        return changes;
    }, [job, formData]);

    // Guard to prevent multiple simultaneous saves
    const isSavingRef = useRef(false);

    // Save current card's changes and close editing
    const saveCardAndClose = useCallback(async () => {
        // Prevent multiple simultaneous saves
        if (isSavingRef.current || !editingCard || !job) return;

        isSavingRef.current = true;
        setEditingCard(null); // Close immediately to prevent re-triggers
        setShowPaymentStatusDropdown(false);

        const changedData = buildChangedData();

        // Only call API if there are actual changes
        if (Object.keys(changedData).length > 0) {
            try {
                await updateJobMutation.mutateAsync(changedData);
            } catch (error) {
                clientLogger.error('Auto-save failed:', error);
            }
        }

        isSavingRef.current = false;
    }, [editingCard, job, buildChangedData, updateJobMutation]);

    // Click outside detection for auto-save
    useEffect(() => {
        if (!editingCard) return;

        const handleClickOutside = (event: MouseEvent) => {
            const currentRef = cardRefs[editingCard];
            const target = event.target as Node;

            // Don't close if clicking inside the card
            if (currentRef?.current && currentRef.current.contains(target)) {
                return;
            }

            // Don't close if clicking on Google Maps autocomplete dropdown
            const pacContainer = document.querySelector('.pac-container');
            if (pacContainer && pacContainer.contains(target)) {
                return;
            }

            saveCardAndClose();
        };

        // Small delay to prevent immediate trigger on click that opened edit mode
        const timeoutId = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside);
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [cardRefs, editingCard, saveCardAndClose]);

    useBackNavigationLayer({
        enabled: !!editingCard,
        onBack: saveCardAndClose,
        state: { editingCard: true },
    });

    // Handle mobile keyboard close - save and close edit mode
    useEffect(() => {
        if (!editingCard) return;

        const viewport = window.visualViewport;
        if (!viewport) return;

        // Track initial viewport height when editing starts. Zoom-corrected: viewport.height
        // shrinks under pinch-zoom exactly like a keyboard, so releasing a zoom (or iOS
        // resetting it when the textarea takes focus) looked like "keyboard closed" and
        // silently saved and exited edit mode the instant you tapped a field while zoomed.
        let lastHeight = layoutViewportHeight(viewport);
        const initialHeight = window.innerHeight;

        const handleViewportResize = () => {
            const currentHeight = layoutViewportHeight(viewport);
            // Keyboard is considered closed when viewport height increases significantly
            // (keyboard was taking up space, now it's gone)
            const heightIncreased = currentHeight > lastHeight + 150;
            const nearFullHeight = currentHeight >= initialHeight * 0.9;

            if (heightIncreased && nearFullHeight) {
                saveCardAndClose();
            }
            lastHeight = currentHeight;
        };

        viewport.addEventListener('resize', handleViewportResize);

        return () => {
            viewport.removeEventListener('resize', handleViewportResize);
        };
    }, [editingCard, saveCardAndClose]);

    const saveAddressAndCloseRef = useRef(saveAddressAndClose);
    saveAddressAndCloseRef.current = saveAddressAndClose;

    const handleAddressBack = useCallback(() => {
        const pacContainers = document.querySelectorAll('.pac-container');
        pacContainers.forEach((el) => {
            (el as HTMLElement).style.display = 'none';
            (el as HTMLElement).style.visibility = 'hidden';
        });
        addressInputRef.current?.blur();
        saveAddressAndCloseRef.current();
    }, []);

    useBackNavigationLayer({
        enabled: isEditingAddress,
        onBack: handleAddressBack,
        state: { editingAddress: true },
    });

    // Reposition pac-container above keyboard when editing address on mobile
    useEffect(() => {
        if (!isEditingAddress) return;

        const viewport = window.visualViewport;
        if (!viewport) return;

        const repositionPacContainer = () => {
            const pacContainer = document.querySelector('.pac-container') as HTMLElement;
            if (!pacContainer) return;
            const viewportHeight = viewport.height;
            const maxBottom = viewportHeight - 8;
            const pacRect = pacContainer.getBoundingClientRect();
            if (pacRect.bottom > maxBottom) {
                pacContainer.style.maxHeight = `${maxBottom - pacRect.top}px`;
            }
        };

        viewport.addEventListener('resize', repositionPacContainer);
        return () => viewport.removeEventListener('resize', repositionPacContainer);
    }, [isEditingAddress]);

    // Scroll address section below the modal header when editing starts
    useEffect(() => {
        if (!isEditingAddress) return;
        requestAnimationFrame(() => {
            addressSectionRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
        });
    }, [isEditingAddress]);

    useBackNavigationLayer({
        enabled: showContactsModal,
        onBack: () => {
            setShowContactsModal(false);
        },
        state: { contactsModal: true },
    });

    useBackNavigationLayer({
        enabled: showCallsModal,
        onBack: () => {
            setShowCallsModal(false);
        },
        state: { callsModal: true },
    });

    useBackNavigationLayer({
        enabled: isOpen && !editingCard && !isEditingAddress,
        onBack: onClose,
        state: { jobDetailsModal: true },
    });

    const handleCardBlur = useCallback((card: Exclude<EditableCard, null>) => (event: React.FocusEvent<HTMLElement>) => {
        const nextTarget = event.relatedTarget as Node | null;
        const currentRef = cardRefs[card];

        if (nextTarget && currentRef?.current?.contains(nextTarget)) {
            return;
        }

        if (editingCard === card) {
            void saveCardAndClose();
        }
    }, [cardRefs, editingCard, saveCardAndClose]);

    // ✓ / ✕ under the Description and Notes boxes. onPointerDown keeps focus in the text box,
    // so tapping a button doesn't trigger the blur auto-save first.
    const renderTextEditActions = () => (
        <div className="mt-2 flex justify-end gap-2">
            <button
                type="button"
                onPointerDown={(e) => e.preventDefault()}
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                    e.stopPropagation();
                    cancelAllEdits();
                }}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-500 shadow-sm active:bg-gray-100"
                aria-label="Cancel"
                title="Cancel"
            >
                <X className="h-4 w-4" />
            </button>
            <button
                type="button"
                onPointerDown={(e) => e.preventDefault()}
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                    e.stopPropagation();
                    void saveCardAndClose();
                }}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-green-600 text-white shadow-sm active:bg-green-700"
                aria-label="Save"
                title="Save"
            >
                <Check className="h-4 w-4" />
            </button>
        </div>
    );

    // Monitor the address input field for external changes (like Google Maps autocomplete)
    useEffect(() => {
        const inputElement = addressInputRef.current;
        if (!inputElement || !isEditMode) return;

        const handleInput = (e: Event) => {
            const target = e.target as HTMLInputElement;
            // Only update if the value is different from current form data
            if (target.value !== (formData.address || '')) {
                handleInputChange('address', target.value);
            }
        };

        const handleChange = (e: Event) => {
            const target = e.target as HTMLInputElement;
            // Final update on change/blur events
            if (target.value !== (formData.address || '')) {
                handleInputChange('address', target.value);
            }
        };

        // Listen for input events (real-time updates)
        inputElement.addEventListener('input', handleInput);
        // Listen for change events (final updates)
        inputElement.addEventListener('change', handleChange);
        inputElement.addEventListener('blur', handleChange);

        return () => {
            inputElement.removeEventListener('input', handleInput);
            inputElement.removeEventListener('change', handleChange);
            inputElement.removeEventListener('blur', handleChange);
        };
    }, [formData.address, handleInputChange, isEditMode]);





    // Helper function to convert empty strings to null recursively
    // Excludes "notes" field which should remain as empty string
    const convertEmptyStringsToNull = (obj: any, parentKey?: string): any => {
        if (obj === null || obj === undefined) {
            return obj;
        }

        if (typeof obj === 'string') {
            // Keep empty string for "notes" field, convert others to null
            if (parentKey === 'notes') {
                return obj;
            }
            return obj === '' ? null : obj;
        }

        if (Array.isArray(obj)) {
            return obj.map(item => convertEmptyStringsToNull(item, parentKey));
        }

        if (typeof obj === 'object') {
            const converted: any = {};
            for (const key in obj) {
                if (Object.prototype.hasOwnProperty.call(obj, key)) {
                    converted[key] = convertEmptyStringsToNull(obj[key], key);
                }
            }
            return converted;
        }

        return obj;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!job) return;

        // All fields are optional except Company (which comes from the job itself)
        const newErrors: Record<string, string> = {};

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        // Build changed data and include extra fields if modified
        const changedData = buildChangedData();

        // Add extra_fields if they were modified
        if (Object.keys(extraFields).length > 0 && JSON.stringify(extraFields) !== JSON.stringify(job.extra_fields)) {
            changedData.extra_fields = extraFields;
        }

        // Convert all empty strings to null
        const cleanedData = convertEmptyStringsToNull(changedData) as JobMergePatchDTO;

        // Remove undefined values (but keep null values)
        Object.keys(cleanedData).forEach(key => {
            const value = cleanedData[key as keyof JobMergePatchDTO];
            if (value === undefined) {
                delete cleanedData[key as keyof JobMergePatchDTO];
            }
        });

        // Only call API if there are actual changes
        if (Object.keys(cleanedData).length > 0) {
            updateJobMutation.mutate(cleanedData);
        } else {
            // No changes, just exit edit mode
            setIsEditMode(false);
        }
    };

    // Fetch the invoice for this job (to check for signature status)
    const { data: jobInvoice } = useQuery<InvoiceDto | null>({
        queryKey: ['invoice', job?.id],
        queryFn: () => job?.id ? apiService.getInvoiceByJobId(job.id).catch(() => null) : Promise.resolve(null),
        enabled: isOpen && !!job?.id,
    });

    const handleJobItemsChange = async (operations: JobItemOperationDTO[]) => {
        if (!job) return;

        // Check if the job has a signed (non-voided) invoice
        if (jobInvoice?.signature && !jobInvoice?.is_signature_voided) {
            const wouldChangeAmount = operations.some(op => {
                if (op.operation === 'Add' || op.operation === 'Remove') return true;
                if (op.operation === 'Edit' && op.item.id) {
                    const existing = (job.items || []).find((i: any) => i.id === op.item.id);
                    if (!existing) return true;
                    return (
                        existing.price !== op.item.price ||
                        existing.quantity !== op.item.quantity ||
                        existing.discount !== op.item.discount ||
                        existing.taxable !== op.item.taxable
                    );
                }
                return false;
            });

            if (wouldChangeAmount) {
                return new Promise<void>((resolve, reject) => {
                    setPendingItemOperations(operations);
                    pendingItemPromiseRef.current = { resolve, reject };
                    setShowItemEditSignatureWarning(true);
                });
            }
        }

        await executeItemOperations(operations);
    };

    const executeItemOperations = async (operations: JobItemOperationDTO[]) => {
        if (!job) return;

        // Apply operations to current items to compute new items list
        let newItems = [...(job.items || [])];
        for (const op of operations) {
            if (op.operation === 'Add') {
                newItems.push(op.item);
            } else if (op.operation === 'Remove' && op.item.id) {
                newItems = newItems.filter(item => (item as any).id !== op.item.id);
            } else if (op.operation === 'Edit' && op.item.id) {
                newItems = newItems.map(item =>
                    (item as any).id === op.item.id ? op.item : item
                );
            }
        }

        // Calculate the new total (including tax)
        const taxRate = companyInvoiceSettings?.tax_rate || 0;
        const newTotal = calculateTotal(newItems, taxRate);

        try {
            await updateJobMutation.mutateAsync({
                item_operations: operations,
                total: newTotal
            });
            addNotification(notificationService.success('Success', 'Items updated successfully'));
        } catch (error) {
            clientLogger.error('Failed to update job items:', error);
            addNotification(notificationService.error('Error', 'Failed to update items'));
            throw error;
        }
    };

    const handleConfirmItemEditSignatureWarning = async () => {
        if (pendingItemOperations) {
            try {
                await executeItemOperations(pendingItemOperations);
                pendingItemPromiseRef.current?.resolve();
            } catch (e) {
                pendingItemPromiseRef.current?.reject(e as Error);
            } finally {
                setPendingItemOperations(null);
                pendingItemPromiseRef.current = null;
            }
        }
    };

    const handleCancelItemEditSignatureWarning = () => {
        setShowItemEditSignatureWarning(false);
        setPendingItemOperations(null);
        pendingItemPromiseRef.current?.reject(new Error('cancelled'));
        pendingItemPromiseRef.current = null;
    };

    const handleImagesUpdated = () => {
        queryClient.invalidateQueries({ queryKey: ['jobAttachments', job?.id] });
        queryClient.invalidateQueries({ queryKey: ['jobs'] });
        if (onJobUpdated && job) {
            onJobUpdated({ ...job });
        }
    };

    // Close status dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
                setShowStatusDropdown(false);
            }
        };

        if (showStatusDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showStatusDropdown]);

    // Close more menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
                setShowMoreMenu(false);
            }
        };

        if (showMoreMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
        setShowSmsSubmenu(false); // never reopen the menu on a stale submenu
    }, [showMoreMenu]);

    // Close job type dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (jobTypeDropdownRef.current && !jobTypeDropdownRef.current.contains(event.target as Node)) {
                setShowJobTypeDropdown(false);
            }
        };

        if (showJobTypeDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showJobTypeDropdown]);

    // Close contact call / SMS pickers when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (contactPhoneActionsRef.current && !contactPhoneActionsRef.current.contains(event.target as Node)) {
                setShowContactPhoneCallMenu(false);
                setShowContactPhoneSmsMenu(false);
            }
        };

        if (showContactPhoneCallMenu || showContactPhoneSmsMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showContactPhoneCallMenu, showContactPhoneSmsMenu]);

    // Recalculate open-up vs open-down when viewport scrolls/resizes while a picker is open
    useEffect(() => {
        if (!showContactPhoneCallMenu && !showContactPhoneSmsMenu) return;

        const update = () => {
            if (showContactPhoneCallMenu && contactCallMenuButtonRef.current) {
                setContactCallMenuOpenUp(shouldOpenContactPhoneMenuUpward(contactCallMenuButtonRef.current));
            }
            if (showContactPhoneSmsMenu && contactSmsMenuButtonRef.current) {
                setContactSmsMenuOpenUp(shouldOpenContactPhoneMenuUpward(contactSmsMenuButtonRef.current));
            }
        };

        update();
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, true);
        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
        };
    }, [showContactPhoneCallMenu, showContactPhoneSmsMenu]);

    // Estimate created from this job (job kept or deleted): close the job and open the estimate,
    // which links back to the job when it was kept.
    const handleEstimateCreated = (result: ConvertJobToEstimateResultDto) => {
        setShowConvertConfirm(false);
        setShowMoreMenu(false);
        queryClient.invalidateQueries({ queryKey: ['jobs'] });
        queryClient.invalidateQueries({ queryKey: ['estimates'] });
        queryClient.invalidateQueries({ queryKey: ['job-estimates'] });
        openLinkedEstimate(result.estimate_id);
    };

    const openLinkedEstimate = (estimateId: string) => {
        onClose();
        openEstimate(estimateId);
    };

    // Reset edit mode when modal closes
    useEffect(() => {
        if (!isOpen) {
            setIsEditMode(false);
            setEditingCard(null);
            setShowContactsModal(false);
            setErrors({});
            setFormData({});
            setExtraFields({});

            setShowMetaInfo(false);
            setShowPaymentsExpanded(false);
            setShowExpensesExpanded(false);
            setShowFullNotes(false);
            setShowStatusDropdown(false);
            setShowPaymentStatusDropdown(false);
            setShowJobTypeDropdown(false);
            setShowContactPhoneCallMenu(false);
            setShowContactPhoneSmsMenu(false);
        }
    }, [isOpen]);

    if (!isOpen || !job) return null;



    const getPaymentStatusColor = (status: string) => {
        switch (status) {
            case 'PaidFully':
                return 'bg-success-100 text-success-800';
            case 'PartiallyPaid':
                return 'bg-warning-100 text-warning-800';
            case 'NotPaid':
                return 'bg-error-100 text-error-800';
            case 'PaymentFailed':
                return 'bg-error-100 text-error-800';
            default:
                return 'bg-secondary-100 text-secondary-800';
        }
    };

    const formatDateRange = (start?: string | null, end?: string | null) =>
        formatDateTimeRangeInCompanyTimeZone(start, end, companyTimeZone);

    const buildRangeUpdate = (start?: string | null, end?: string | null): JobMergePatchDTO => {
        const currentStart = job?.date_executed_start;
        const currentEnd = job?.date_executed_end;
        const nextStart = start !== undefined ? start : currentStart;
        const nextEnd = end !== undefined ? end : currentEnd;

        return {
            date_executed_start: nextStart ?? (nextEnd ?? null),
            date_executed_end: nextEnd ?? (nextStart ?? null),
        };
    };

    const getStatusStyle = (job: JobDTO) => {
        const backgroundColor = job.status_color || '#808080';
        // Calculate text color based on background brightness
        const hex = backgroundColor.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
        const textColor = brightness > 128 ? '#000000' : '#FFFFFF';

        return {
            backgroundColor: backgroundColor,
            color: textColor
        };
    };

    const handleNavigate = async () => {
        if (!job.address) return;
        await openNavigation(job.address);
    };

    const handleCopyAddress = async () => {
        if (!job.address) return;

        // Use cross-platform clipboard utility (works on native and web)
        const success = await copyToClipboard(job.address);
        if (success) {
            addNotification(notificationService.success('Copied', 'Address copied to clipboard'));
        } else {
            addNotification(notificationService.error('Error', 'Failed to copy address'));
        }
    };

    // Handle status change
    const handleStatusChange = async (newStatusId: string) => {
        if (!job || !job.company?.id) return;

        // Find the current status ID from the job
        const currentStatusId = jobStatuses?.find((status: JobStatusDTO) => status.name === job.status)?.id;

        // Don't send API request if status didn't change
        if (currentStatusId === newStatusId) {
            setShowStatusDropdown(false);
            return;
        }

        try {
            // Update the job with new status
            await updateJobStatusMutation.mutateAsync(newStatusId);
            setShowStatusDropdown(false);
        } catch (error) {
            clientLogger.error('Error updating job status:', error);
        }
    };

    const renderPrimaryContactNotes = () => {
        const notes = job.contacts?.[0]?.notes?.replace(/\s+/g, ' ').trim();
        if (!notes) return null;
        const preview = notes.length > 40 ? `${notes.slice(0, 40)}...` : notes;
        return <p className="text-xs text-gray-500 mt-1 italic">"{preview}"</p>;
    };

    const renderPrimaryContactPhoneActions = () => {
        const contact = job.contacts?.[0]?.contact;
        if (!contact?.phone) return null;
        const additionalTrimmed = contact.additional_phone?.trim();
        const hasTwoNumbers = Boolean(additionalTrimmed);

        const openSmsTo = (recipientPhone: string, which: 'primary' | 'additional') => {
            setShowContactPhoneCallMenu(false);
            setShowContactPhoneSmsMenu(false);
            setSmsSelectorState({
                isOpen: true,
                recipientPhone,
                recipientName: contact.name || 'Client',
                recipientType: 'Client',
                jobId: job.id,
                masked: contact.phone_masked,
                contactId: contact.id,
                contactWhich: which,
            });
        };

        const contactPickerOpen = hasTwoNumbers && (showContactPhoneCallMenu || showContactPhoneSmsMenu);

        const callMenuPanelClass =
            contactCallMenuOpenUp
                ? 'bottom-full mb-1 animate-in fade-in slide-in-from-bottom-2'
                : 'top-full mt-1 animate-in fade-in slide-in-from-top-2';
        const smsMenuPanelClass =
            contactSmsMenuOpenUp
                ? 'bottom-full mb-1 animate-in fade-in slide-in-from-bottom-2'
                : 'top-full mt-1 animate-in fade-in slide-in-from-top-2';

        return (
            <div
                ref={contactPhoneActionsRef}
                className={`flex gap-2 relative${contactPickerOpen ? ' z-[60]' : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                {hasTwoNumbers ? (
                    <div className="relative">
                        <button
                            ref={contactCallMenuButtonRef}
                            type="button"
                            onClick={(e) => {
                                setShowContactPhoneSmsMenu(false);
                                if (showContactPhoneCallMenu) {
                                    setShowContactPhoneCallMenu(false);
                                } else {
                                    setContactCallMenuOpenUp(shouldOpenContactPhoneMenuUpward(e.currentTarget));
                                    setShowContactPhoneCallMenu(true);
                                }
                            }}
                            className="p-2 bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition-colors shadow-sm flex items-center gap-0.5"
                            aria-haspopup="listbox"
                            aria-expanded={showContactPhoneCallMenu}
                        >
                            <Phone className="h-3.5 w-3.5" />
                            <ChevronDown className={`h-3 w-3 transition-transform ${showContactPhoneCallMenu ? 'rotate-180' : ''}`} />
                        </button>
                        {showContactPhoneCallMenu && (
                            <div
                                className={`absolute right-0 z-50 min-w-[12rem] max-h-48 overflow-y-auto bg-white rounded-lg shadow-xl border border-gray-200 ${callMenuPanelClass}`}
                                role="listbox"
                            >
                                <button
                                    type="button"
                                    className="block w-full px-3 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors border-b border-gray-100"
                                    onClick={() => {
                                        setShowContactPhoneCallMenu(false);
                                        // Masked too: the sheet dials at once with one line, or offers the choice.
                                        setCallSheetState({ isOpen: true, toE164: contact.phone, displayName: contact.name || formatPhoneNumber(contact.phone), jobId: job?.id, masked: contact.phone_masked, contactId: contact.id, contactWhich: 'primary' });
                                    }}
                                >
                                    <span className="text-xs text-gray-500">Primary</span>
                                    <span className="block font-medium text-gray-900">{formatPhoneNumber(contact.phone)}</span>
                                </button>
                                <button
                                    type="button"
                                    className="block w-full px-3 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors"
                                    onClick={() => {
                                        setShowContactPhoneCallMenu(false);
                                        setCallSheetState({ isOpen: true, toE164: additionalTrimmed!, displayName: contact.name || formatPhoneNumber(additionalTrimmed), jobId: job?.id, masked: contact.phone_masked, contactId: contact.id, contactWhich: 'additional' });
                                    }}
                                >
                                    <span className="text-xs text-gray-500">Additional</span>
                                    <span className="block font-medium text-gray-900">{formatPhoneNumber(additionalTrimmed)}</span>
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={() => {
                            setCallSheetState({ isOpen: true, toE164: contact.phone, displayName: contact.name || formatPhoneNumber(contact.phone), jobId: job?.id, masked: contact.phone_masked, contactId: contact.id, contactWhich: 'primary' });
                        }}
                        className="p-2 bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition-colors shadow-sm"
                    >
                        <Phone className="h-3.5 w-3.5" />
                    </button>
                )}
                {hasTwoNumbers ? (
                    <div className="relative">
                        <button
                            ref={contactSmsMenuButtonRef}
                            type="button"
                            onClick={(e) => {
                                setShowContactPhoneCallMenu(false);
                                if (showContactPhoneSmsMenu) {
                                    setShowContactPhoneSmsMenu(false);
                                } else {
                                    setContactSmsMenuOpenUp(shouldOpenContactPhoneMenuUpward(e.currentTarget));
                                    setShowContactPhoneSmsMenu(true);
                                }
                            }}
                            className="p-2 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors shadow-sm flex items-center gap-0.5"
                            aria-haspopup="listbox"
                            aria-expanded={showContactPhoneSmsMenu}
                        >
                            <MessageSquare className="h-3.5 w-3.5" />
                            <ChevronDown className={`h-3 w-3 transition-transform ${showContactPhoneSmsMenu ? 'rotate-180' : ''}`} />
                        </button>
                        {showContactPhoneSmsMenu && (
                            <div
                                className={`absolute right-0 z-50 min-w-[12rem] max-h-48 overflow-y-auto bg-white rounded-lg shadow-xl border border-gray-200 ${smsMenuPanelClass}`}
                                role="listbox"
                            >
                                <button
                                    type="button"
                                    onClick={() => openSmsTo(contact.phone, 'primary')}
                                    className="block w-full px-3 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors border-b border-gray-100"
                                >
                                    <span className="text-xs text-gray-500">Primary</span>
                                    <span className="block font-medium text-gray-900">{formatPhoneNumber(contact.phone)}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => openSmsTo(additionalTrimmed!, 'additional')}
                                    className="block w-full px-3 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors"
                                >
                                    <span className="text-xs text-gray-500">Additional</span>
                                    <span className="block font-medium text-gray-900">{formatPhoneNumber(additionalTrimmed)}</span>
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={() => openSmsTo(contact.phone, 'primary')}
                        className="p-2 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors shadow-sm"
                    >
                        <MessageSquare className="h-3.5 w-3.5" />
                    </button>
                )}
            </div>
        );
    };

    const modalMaxWidth = isMobile ? 'max-w-full' : 'max-w-4xl'; // Reduced max-width for command center feel

    return createPortal(
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-0 sm:p-4" style={layer?.style}>
            <div className={`bg-gray-50 sm:rounded-xl shadow-2xl ${modalMaxWidth} w-full h-full sm:h-[90vh] overflow-hidden flex flex-col relative safe-area-top`}>
                {/* This modal covers the whole page, including the Dashboard's status strip — repeat it here. */}
                <div className="shrink-0 relative z-30"><SubscriptionBanner /></div>

                {/* Header - Compact (z above scroll + Maps iframe so status/type menus aren't covered) */}
                <div className="relative z-30 isolate bg-white border-b border-gray-200 shrink-0 touch-none">
                    <div className="px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {/* Status or Canceled Badge */}
                            {job.canceled_at ? (
                                <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-red-100 text-red-700 rounded">
                                    Canceled {job.cancel_reason && ` (${job.cancel_reason})`}
                                </span>
                            ) : (
                                <div className="relative" ref={statusDropdownRef}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (!canEditField('status')) return;
                                            if (showStatusDropdown) {
                                                setShowStatusDropdown(false);
                                                return;
                                            }
                                            if (isAnyEditActive()) {
                                                cancelAllEdits();
                                                return;
                                            }
                                            if (!isEditMode) {
                                                setShowStatusDropdown(true);
                                            }
                                        }}
                                        disabled={isEditMode || !canEditField('status')}
                                        className={`flex items-center justify-center gap-1.5 h-8 px-3 rounded text-xs font-bold shadow-sm transition-all ${!isEditMode && canEditField('status') ? 'hover:shadow-md active:scale-95 cursor-pointer' : 'cursor-default'
                                            }`}
                                        style={getStatusStyle(job)}
                                    >
                                        <span>{job.status || 'No Status'}</span>
                                        {!isEditMode && canEditField('status') && <ChevronDown className="h-3 w-3" />}
                                    </button>

                                    {/* Status Dropdown */}
                                    {showStatusDropdown && jobStatuses && jobStatuses.length > 0 && (
                                        <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50 animate-in fade-in slide-in-from-top-2">
                                            {jobStatuses.map((status: JobStatusDTO) => {
                                                const statusBgColor = status.color || '#808080';
                                                const hex = statusBgColor.replace('#', '');
                                                const r = parseInt(hex.substr(0, 2), 16);
                                                const g = parseInt(hex.substr(2, 2), 16);
                                                const b = parseInt(hex.substr(4, 2), 16);
                                                const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
                                                const textColor = brightness > 128 ? '#000000' : '#FFFFFF';

                                                return (
                                                    <button
                                                        key={status.id}
                                                        type="button"
                                                        onClick={() => handleStatusChange(status.id)}
                                                        className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors flex items-center gap-2"
                                                    >
                                                        <div
                                                            className="flex-1 px-2 py-1 rounded text-xs font-semibold"
                                                            style={{
                                                                backgroundColor: statusBgColor,
                                                                color: textColor
                                                            }}
                                                        >
                                                            {status.name}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Editable Job Type */}
                            <div className="relative" ref={jobTypeDropdownRef}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (!canEditField('job_type')) return;
                                        if (showJobTypeDropdown) {
                                            setShowJobTypeDropdown(false);
                                            return;
                                        }
                                        if (isAnyEditActive()) {
                                            cancelAllEdits();
                                            return;
                                        }
                                        if (!jobTypesLoading) {
                                            setShowJobTypeDropdown(true);
                                        }
                                    }}
                                    disabled={isEditMode || !canEditField('job_type')}
                                    className={`text-left transition-all ${!isEditMode && canEditField('job_type') ? 'hover:bg-gray-100 rounded-lg p-1 -m-1' : ''}`}
                                >
                                    <div className="flex items-center gap-1">
                                        <h2 className="text-lg font-bold text-gray-900 leading-tight">
                                            {job.job_type?.name || 'Service Call'}
                                        </h2>
                                        {!isEditMode && canEditField('job_type') && <ChevronDown className={`h-3 w-3 text-gray-400 transition-transform ${showJobTypeDropdown ? 'rotate-180' : ''}`} />}
                                    </div>
                                    <p className='text-xs text-gray-500'>
                                        Job #{job.invoice_number || job.id.slice(0, 8)}
                                    </p>
                                </button>

                                {/* Job Type Dropdown */}
                                {showJobTypeDropdown && jobTypes && (
                                    <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50 max-h-60 overflow-y-auto animate-in fade-in slide-in-from-top-2">
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                setShowJobTypeDropdown(false);
                                                if (job.job_type?.id) {
                                                    try {
                                                        await updateJobMutation.mutateAsync({ job_type_id: '' });
                                                    } catch (error) {
                                                        clientLogger.error('Failed to update job type:', error);
                                                    }
                                                }
                                            }}
                                            className={`w-full px-3 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors border-b border-gray-100 ${!job.job_type?.id ? 'bg-blue-50 text-blue-700 font-medium' : ''}`}
                                        >
                                            No Job Type
                                        </button>
                                        {jobTypes?.map((jobType: JobTypeBasicDTO) => (
                                            <button
                                                key={jobType.id}
                                                type="button"
                                                onClick={async () => {
                                                    setShowJobTypeDropdown(false);
                                                    if (jobType.id !== job.job_type?.id) {
                                                        try {
                                                            await updateJobMutation.mutateAsync({ job_type_id: jobType.id });
                                                        } catch (error) {
                                                            clientLogger.error('Failed to update job type:', error);
                                                        }
                                                    }
                                                }}
                                                className={`w-full px-3 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors ${jobType.id === job.job_type?.id ? 'bg-blue-50 text-blue-700 font-medium' : ''}`}
                                            >
                                                {jobType.name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={onClose} className="p-2 text-gray-500 hover:bg-gray-100 rounded-full">
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Pending Approval Banner */}
                {job.pending_approval && (
                    <div className="flex items-center justify-between px-4 py-2 bg-amber-50 border-b border-amber-200 shrink-0">
                        <div className="flex items-center gap-2 text-amber-800 text-sm font-medium">
                            <Clock className="h-4 w-4" />
                            Pending admin approval
                        </div>
                        {!isTechnician && (
                            <button
                                type="button"
                                onClick={async () => {
                                    try {
                                        await apiService.approveJob(job.id);
                                        queryClient.invalidateQueries({ queryKey: ['jobs'] });
                                        queryClient.invalidateQueries({ queryKey: ['job', job.id] });
                                        if (onJobUpdated) {
                                            onJobUpdated({ ...job, pending_approval: false });
                                        }
                                        addNotification(notificationService.success('Job approved'));
                                    } catch (error: any) {
                                        addNotification(notificationService.error('Failed to approve job'));
                                    }
                                }}
                                className="px-3 py-1 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors"
                            >
                                Approve
                            </button>
                        )}
                    </div>
                )}

                {/* Main Content - Scrollable (z-0 keeps Maps canvas/iframe under the header when menus overlap this area) */}
                <div className={`relative flex-1 overflow-y-auto overscroll-y-none ${isKeyboardOpen ? '' : 'pb-footer-safe'} ${isEditingAddress ? 'z-40' : 'z-0'}`}> {/* pb-footer-safe accounts for sticky footer + safe area */}
                    <form ref={formRef} onSubmit={handleSubmit}>

                        {/* Hero Section: Map & Location */}
                        <div className="relative z-0 w-full h-[25vh] bg-gray-200">
                            <MapWidget address={job.address || ''} height="100%" />
                        </div>

                        {/* Address editing overlay - fixed at top so autocomplete appears above keyboard */}
                        {isEditingAddress && (
                            <div
                                className="fixed inset-0 z-40 bg-black/30"
                                onClick={() => saveAddressAndClose()}
                            />
                        )}
                        <div
                            ref={addressSectionRef}
                            className={isEditingAddress
                                ? 'sticky top-0 z-50 bg-white shadow-lg border-b border-gray-200'
                                : `bg-white px-4 py-3 border-b border-gray-200 flex items-center justify-between transition-all ${canEditField('address') ? 'cursor-pointer hover:bg-gray-50' : ''}`
                            }
                            onClick={isEditingAddress ? undefined : () => {
                                if (!canEditField('address')) return;
                                if (isAnyEditActive()) {
                                    cancelAllEdits();
                                    return;
                                }
                                setIsEditingAddress(true);
                                setTimeout(() => addressInputRef.current?.focus(), 50);
                            }}
                        >
                            {/* Editing toolbar */}
                            {isEditingAddress && (
                                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setAddressValue(job.address || '');
                                            setUnitNameValue(job.unit_name || '');
                                            setIsEditingAddress(false);
                                        }}
                                        className="text-sm text-gray-600 font-medium px-2 py-1"
                                    >
                                        Cancel
                                    </button>
                                    <div className="flex items-center gap-1.5 text-gray-700">
                                        <MapPin className="h-4 w-4" />
                                        <span className="text-sm font-semibold">Edit Address</span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => saveAddressAndClose()}
                                        className="flex items-center justify-center h-9 w-9 rounded-full bg-blue-600 text-white shadow-sm hover:bg-blue-700 active:scale-95 transition-colors"
                                        aria-label="Save address"
                                    >
                                        <Check className="h-5 w-5" />
                                    </button>
                                </div>
                            )}

                            <div className={isEditingAddress ? 'px-4 py-3' : 'flex-1 min-w-0 mr-2 relative'}>
                                {/* Always render input so Google Maps autocomplete stays attached */}
                                <input
                                    ref={addressInputRef}
                                    type="text"
                                    value={addressValue}
                                    onChange={(e) => setAddressValue(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    onFocus={(e) => {
                                        const len = e.target.value.length;
                                        e.target.setSelectionRange(len, len);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            const pacContainer = document.querySelector('.pac-container');
                                            const isAutocompleteVisible = pacContainer &&
                                                getComputedStyle(pacContainer).display !== 'none' &&
                                                getComputedStyle(pacContainer).visibility !== 'hidden';
                                            if (!isAutocompleteVisible) {
                                                saveAddressAndClose();
                                            }
                                        } else if (e.key === 'Escape') {
                                            setAddressValue(job.address || '');
                                            setUnitNameValue(job.unit_name || '');
                                            setIsEditingAddress(false);
                                        }
                                    }}
                                    className={isEditingAddress
                                        ? 'w-full text-base text-gray-900 border border-gray-300 rounded-lg p-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none'
                                        : 'w-full text-sm font-semibold text-gray-900 border border-gray-300 rounded-lg p-2 opacity-0 absolute inset-0 pointer-events-none'
                                    }
                                    placeholder="Enter address..."
                                    autoComplete="off"
                                />
                                {/* Unit name input - only visible when editing */}
                                {isEditingAddress && (
                                    <input
                                        ref={unitNameInputRef}
                                        type="text"
                                        value={unitNameValue}
                                        onChange={(e) => setUnitNameValue(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                saveAddressAndClose();
                                            } else if (e.key === 'Escape') {
                                                setAddressValue(job.address || '');
                                                setUnitNameValue(job.unit_name || '');
                                                setIsEditingAddress(false);
                                            }
                                        }}
                                        className="w-full text-base text-gray-900 border border-gray-300 rounded-lg p-3 mt-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                                        placeholder="Unit/Business Name (optional)"
                                        autoComplete="off"
                                    />
                                )}
                                {/* Display text when not editing */}
                                {!isEditingAddress && (
                                    <p className="text-sm font-semibold text-gray-900 line-clamp-2">
                                        {job.address || 'No address provided'}
                                        {job.unit_name && <span className="text-gray-600">, {job.unit_name}</span>}
                                    </p>
                                )}
                            </div>
                            {/* Action buttons only when not editing */}
                            {!isEditingAddress && (
                                <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                                    <button type="button" onClick={handleCopyAddress} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors">
                                        <FileText className="h-4 w-4" />
                                    </button>
                                    <button type="button" onClick={handleNavigate} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 shadow-sm transition-colors">
                                        <Truck className="h-3 w-3" />
                                        Navigate
                                    </button>
                                </div>
                            )}
                        </div>


                        <div className="px-4 pt-2 pb-4 space-y-2">

                            {/* Description Card */}
                            <div
                                ref={descriptionCardRef}
                                className={`bg-white p-3 rounded-xl border shadow-sm transition-all ${editingCard === 'description' ? 'border-blue-400 ring-2 ring-blue-100' : 'border-gray-200'} ${canEditField('description') ? 'cursor-pointer hover:border-gray-300 active:scale-[0.99]' : ''}`}
                                onClick={() => {
                                    if (!canEditField('description')) return;
                                    if (isAnyEditActive()) {
                                        cancelAllEdits();
                                        return;
                                    }
                                    setFormData(prev => ({ ...prev, description: job.description || '' }));
                                    setEditingCard('description');
                                }}
                            >
                                <div className="flex items-center gap-2 mb-2">
                                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Description</h3>
                                    {!job.description && canEditField('description') && <Edit2 className="h-3.5 w-3.5 text-gray-400" />}
                                </div>
                                {editingCard === 'description' ? (
                                    <textarea
                                        autoFocus
                                        value={formData.description ?? job.description ?? ''}
                                        onChange={(e) => handleInputChange('description', e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        onFocus={(e) => {
                                            fitTextareaToText(e.target);
                                            const len = e.target.value.length;
                                            e.target.setSelectionRange(len, len);
                                        }}
                                        onBlur={handleCardBlur('description')}
                                        onInput={(e) => fitTextareaToText(e.currentTarget)}
                                            className="w-full text-sm border-gray-300 rounded-lg p-2 resize-none overflow-hidden"
                                        rows={3}
                                        placeholder="Describe the job..."
                                    />
                                ) : null}
                                {editingCard === 'description' && renderTextEditActions()}
                                {editingCard === 'description' ? null : job.description ? (
                                    <div className="flex items-start gap-2">
                                        <p className="flex-1 text-sm text-gray-800 break-words whitespace-pre-line leading-relaxed">
                                            {job.description}
                                        </p>
                                        {canEditField('description') && <Edit2 className="h-3.5 w-3.5 text-gray-400 shrink-0 mt-0.5" />}
                                    </div>
                                ) : null}
                            </div>

                            {/* Action Grid: Schedule & Technician */}
                            <div className="grid grid-cols-2 gap-3">
                                {/* Schedule Card - Click to open datetime picker directly */}
                                <div
                                    className={`bg-white p-3 rounded-xl border shadow-sm flex flex-col justify-center transition-all border-gray-200 ${canEditField('date') ? 'cursor-pointer hover:border-gray-300 active:scale-[0.98]' : ''}`}
                                    onClick={() => {
                                        if (!canEditField('date')) return;
                                        if (isAnyEditActive()) {
                                            cancelAllEdits();
                                            return;
                                        }
                                        setStartPickerOpen(true);
                                    }}
                                >
                                    <div className="flex items-center gap-2 text-gray-500 mb-1">
                                        <Calendar className="h-4 w-4 text-blue-500" />
                                        <span className="text-xs font-medium">Schedule</span>
                                    </div>
                                    {job.is_asap ? (
                                        <AsapBadge />
                                    ) : (
                                        <p className="text-sm font-bold text-gray-900">{formatDateRange(job.date_executed_start, job.date_executed_end)}</p>
                                    )}
                                    {canEditField('date') && (() => {
                                        if (job.is_asap) {
                                            return (
                                                <div className="mt-2">
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setScheduleExpanded(false);
                                                            setStartPickerOpen(true);
                                                        }}
                                                        className="text-xs text-blue-600 font-medium"
                                                    >
                                                        Set time
                                                    </button>
                                                </div>
                                            );
                                        }
                                        const markAsapButton = (
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setShowConfirmMarkAsap(true);
                                                }}
                                                className="flex items-center gap-0.5 text-xs text-red-600 font-medium text-left"
                                            >
                                                <Zap className="h-3 w-3" />
                                                Mark ASAP
                                            </button>
                                        );
                                        const showRange = scheduleExpanded || isScheduleRange(job.date_executed_start, job.date_executed_end);
                                        if (!showRange) {
                                            // Collapsed: single edit + opt-in end time.
                                            return (
                                                <>
                                                <div className="mt-2 flex justify-between">
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setStartPickerOpen(true);
                                                        }}
                                                        className="text-xs text-blue-600 font-medium"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setScheduleExpanded(true);
                                                            setEndPickerOpen(true);
                                                        }}
                                                        className="text-xs text-blue-600 font-medium"
                                                    >
                                                        Add end time
                                                    </button>
                                                </div>
                                                <div className="mt-1.5">{markAsapButton}</div>
                                                </>
                                            );
                                        }
                                        // Expanded: separate start / end + collapse back to single.
                                        return (
                                            <div className="mt-2 flex flex-col gap-1.5">
                                                <div className="flex justify-between">
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setStartPickerOpen(true);
                                                        }}
                                                        className="text-xs text-blue-600 font-medium"
                                                    >
                                                        Edit Start
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setEndPickerOpen(true);
                                                        }}
                                                        className="text-xs text-blue-600 font-medium"
                                                    >
                                                        Edit End
                                                    </button>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        setScheduleExpanded(false);
                                                        const startVal = scheduleStart || job.date_executed_start || '';
                                                        setScheduleEnd(startVal);
                                                        if (startVal !== job.date_executed_end) {
                                                            try {
                                                                await updateJobMutation.mutateAsync({ date_executed_end: startVal });
                                                            } catch (error) {
                                                                clientLogger.error('Failed to update schedule end:', error);
                                                            }
                                                        }
                                                    }}
                                                    className="text-xs text-blue-600 font-medium text-left"
                                                >
                                                    Use single date & time
                                                </button>
                                                {markAsapButton}
                                            </div>
                                        );
                                    })()}
                                </div>
                                <div className="sr-only" tabIndex={-1}>
                                    <MobileDateTimePicker
                                        open={startPickerOpen}
                                        onClose={() => setStartPickerOpen(false)}
                                        sx={gentleDateTimePickerRootSx}
                                        {...(companyTimeZone ? { timezone: companyTimeZone } : {})}
                                        value={scheduleStartPickerValue}
                                        onChange={async (newValue) => {
                                            if (newValue) {
                                                const newDate = newValue.toISOString();

                                                // Collapsed (single) mode: keep start and end equal.
                                                const single = !(scheduleExpanded || isScheduleRange(job.date_executed_start, job.date_executed_end));
                                                if (single) {
                                                    setScheduleStart(newDate);
                                                    setScheduleEnd(newDate);
                                                    // ASAP: picking a time must clear the flag even if it equals the placeholder.
                                                    if (job.is_asap || newDate !== job.date_executed_start || newDate !== job.date_executed_end) {
                                                        try {
                                                            await updateJobMutation.mutateAsync({
                                                                date_executed_start: newDate,
                                                                date_executed_end: newDate,
                                                                ...(job.is_asap ? { is_asap: false } : {}),
                                                            });
                                                        } catch (error) {
                                                            clientLogger.error('Failed to update schedule:', error);
                                                        }
                                                    }
                                                    return;
                                                }

                                                setScheduleStart(newDate);

                                                // Validate: if new start is after current end, update end too
                                                const startDate = new Date(newDate);
                                                const currentEnd = scheduleEnd || job.date_executed_end;
                                                const endDate = currentEnd ? new Date(currentEnd) : null;
                                                if (endDate && startDate > endDate) {
                                                    setScheduleEnd(newDate);
                                                }

                                                if (newDate !== job.date_executed_start) {
                                                    try {
                                                        // If start is after end, update both
                                                        if (endDate && startDate > endDate) {
                                                            await updateJobMutation.mutateAsync({
                                                                date_executed_start: newDate,
                                                                date_executed_end: newDate
                                                            });
                                                        } else {
                                                            await updateJobMutation.mutateAsync(buildRangeUpdate(newDate, undefined));
                                                        }
                                                    } catch (error) {
                                                        clientLogger.error('Failed to update schedule:', error);
                                                    }
                                                }
                                            }
                                        }}
                                        viewRenderers={{ hours: renderTimeViewClock, minutes: renderTimeViewClock, seconds: renderTimeViewClock }}
                                        slotProps={{
                                            ...gentleDateTimePickerSlotProps,
                                            toolbar: { hidden: false, ...gentleDateTimePickerSlotProps.toolbar },
                                            tabs: { hidden: false, ...gentleDateTimePickerSlotProps.tabs },
                                        }}
                                    />
                                    <MobileDateTimePicker
                                        open={endPickerOpen}
                                        onClose={() => setEndPickerOpen(false)}
                                        sx={gentleDateTimePickerRootSx}
                                        {...(companyTimeZone ? { timezone: companyTimeZone } : {})}
                                        value={scheduleEndPickerValue}
                                        onChange={async (newValue) => {
                                            if (newValue) {
                                                const newDate = newValue.toISOString();
                                                setScheduleEnd(newDate);

                                                // Validate: if new end is before current start, update start too
                                                const endDate = new Date(newDate);
                                                const currentStart = scheduleStart || job.date_executed_start;
                                                const startDate = currentStart ? new Date(currentStart) : null;
                                                if (startDate && endDate < startDate) {
                                                    setScheduleStart(newDate);
                                                }

                                                if (newDate !== job.date_executed_end) {
                                                    try {
                                                        // If end is before start, update both
                                                        if (startDate && endDate < startDate) {
                                                            await updateJobMutation.mutateAsync({
                                                                date_executed_start: newDate,
                                                                date_executed_end: newDate
                                                            });
                                                        } else {
                                                            await updateJobMutation.mutateAsync(buildRangeUpdate(undefined, newDate));
                                                        }
                                                    } catch (error) {
                                                        clientLogger.error('Failed to update schedule end:', error);
                                                    }
                                                }
                                            }
                                        }}
                                        viewRenderers={{ hours: renderTimeViewClock, minutes: renderTimeViewClock, seconds: renderTimeViewClock }}
                                        slotProps={{
                                            ...gentleDateTimePickerSlotProps,
                                            toolbar: { hidden: false, ...gentleDateTimePickerSlotProps.toolbar },
                                            tabs: { hidden: false, ...gentleDateTimePickerSlotProps.tabs },
                                        }}
                                    />
                                </div>

                                {/* Technician Card - hidden for technician role */}
                                {!isTechnician && (
                                    <div className="bg-white p-3 rounded-xl border shadow-sm flex flex-col gap-1 transition-all border-gray-200 relative">
                                        {/* Row 1 - label */}
                                        <div className="flex items-center gap-2 text-gray-500">
                                            <User className="h-4 w-4 text-orange-500" />
                                            <span className="text-xs font-medium">Technician</span>
                                        </div>
                                        {/* Row 2 - selector (full width) */}
                                        <CustomSelect
                                            variant="ghost"
                                            value={job.assigned_technician?.id || ''}
                                            onChange={async (val) => {
                                                if (val !== (job.assigned_technician?.id || '')) {
                                                    try {
                                                        await updateJobMutation.mutateAsync({ assigned_technician_id: val || null });
                                                    } catch (error) {
                                                        clientLogger.error('Failed to update technician:', error);
                                                    }
                                                }
                                            }}
                                            options={buildTechnicianSelectOptions(
                                                technicians,
                                                showInactiveTechnicians,
                                                job.assigned_technician?.id,
                                                { value: '', label: 'Unassigned' },
                                            )}
                                            disabled={!canEditField('technician') || techniciansLoading}
                                            footerAction={hasInactiveTechnicians(technicians) && !showInactiveTechnicians
                                                ? { label: 'Show inactive technicians...', onClick: () => setShowInactiveTechnicians(true) }
                                                : undefined}
                                            renderValue={(_option) => {
                                                const assignedTechnician = assignedTechnicianDetails || technicians?.find((e: Technician) => e.id === job.assigned_technician?.id) || job.assigned_technician;
                                                if (!assignedTechnician) return <span className="text-sm font-bold text-gray-900">Unassigned</span>;
                                                const status = (assignedTechnician as Technician).status;
                                                const isActive = !status || status === 'Active';
                                                return (
                                                    <span className="block w-full min-w-0">
                                                        <span className="block text-sm font-bold text-gray-900 truncate">{assignedTechnician.name}</span>
                                                        {!isActive && (
                                                            <span className="block text-xs text-amber-600 font-medium">{status === 'OnLeave' ? 'On Leave' : status}</span>
                                                        )}
                                                    </span>
                                                );
                                            }}
                                        />
                                        {/* Row 3 - call/SMS icons */}
                                        {(() => {
                                            const technician = assignedTechnicianDetails || technicians?.find((e: Technician) => e.id === job.assigned_technician?.id);
                                            // Show the message button if we can SMS (phone) or internally message (user account).
                                            if (!technician?.phone && !technician?.user_id) return null;
                                            return (
                                                <div className="flex gap-2">
                                                    {technician.phone && (
                                                    <a href={`tel:${technician.phone}`} className="p-2 bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition-colors shadow-sm">
                                                        <Phone className="h-3.5 w-3.5" />
                                                    </a>
                                                    )}
                                                    <button
                                                        onClick={() => setSmsSelectorState({
                                                            isOpen: true,
                                                            recipientPhone: technician.phone || '',
                                                            recipientName: technician.name || 'Technician',
                                                            recipientType: 'Technician',
                                                            jobId: job.id,
                                                            technicianUserId: technician.user_id
                                                        })}
                                                        className="p-2 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors shadow-sm"
                                                    >
                                                        <MessageSquare className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}

                                {/* Contact Card in grid - only for technician role */}
                                {isTechnician && (
                                    <div
                                        className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-between transition-all duration-200 cursor-pointer hover:border-gray-300 active:scale-[0.98]"
                                        onClick={() => {
                                            if (isAnyEditActive()) {
                                                cancelAllEdits();
                                                return;
                                            }
                                            setShowContactsModal(true);
                                        }}
                                    >
                                        <div className="flex items-center justify-between mb-1">
                                            <div className="flex items-center gap-2 text-gray-500">
                                                <User className="h-4 w-4 text-purple-500" />
                                                <span className="text-xs font-medium">Contact</span>
                                            </div>
                                            <ChevronDown className="h-3 w-3 text-gray-400" />
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <p className="text-sm font-bold text-gray-900 truncate max-w-[55%]">
                                                {job.contacts?.[0]?.contact ? (job.contacts[0].contact.name || 'No contact name') : 'No contact'}
                                            </p>
                                            {renderPrimaryContactPhoneActions()}
                                        </div>
                                        {renderPrimaryContactNotes()}
                                        {job.contacts && job.contacts.length > 1 && (
                                            <p className="text-xs text-gray-500 mt-1">+{job.contacts.length - 1} more contacts</p>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Contact Card - hidden for technician role (shown in grid instead) */}
                            {!isTechnician && (
                                <div
                                    className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-between transition-all duration-200 cursor-pointer hover:border-gray-300 active:scale-[0.98]"
                                    onClick={() => {
                                        if (isAnyEditActive()) {
                                            cancelAllEdits();
                                            return;
                                        }
                                        setShowContactsModal(true);
                                    }}
                                >
                                    <div className="flex items-center justify-between mb-1">
                                        <div className="flex items-center gap-2 text-gray-500">
                                            <User className="h-4 w-4 text-purple-500" />
                                            <span className="text-xs font-medium">Contact</span>
                                        </div>
                                        <ChevronDown className="h-3 w-3 text-gray-400" />
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <p className="text-sm font-bold text-gray-900 truncate max-w-[55%]">
                                            {job.contacts?.[0]?.contact ? (job.contacts[0].contact.name || 'No contact name') : 'No contact'}
                                        </p>
                                        {renderPrimaryContactPhoneActions()}
                                    </div>
                                    {renderPrimaryContactNotes()}
                                    {/* Show contact count if multiple */}
                                    {job.contacts && job.contacts.length > 1 && (
                                        <p className="text-xs text-gray-500 mt-1">+{job.contacts.length - 1} more contacts</p>
                                    )}

                                                                    {/* Call Masking - admin only */}
                                {/* Admin only, and only on plans that include call masking —
                                    otherwise the server backstop resets the flag on save and
                                    the toggle silently appears not to stick. */}
                                {isAdmin && isFeatureAllowed('voip.call-masking') && (
                                    <div onClick={(e) => e.stopPropagation()}>
                                        <div className="my-3 border-t border-gray-100" />
                                        <CallMaskingOptions
                                            variant="job"
                                            idPrefix="job_masking_mobile"
                                            withEnableAll
                                            route={job.route_calls_to_technician ?? false}
                                            hide={job.hide_contact_phone ?? false}
                                            onChange={async (field, value) => {
                                                try {
                                                    await updateJobMutation.mutateAsync(field === 'route'
                                                        ? { route_calls_to_technician: value }
                                                        : { hide_contact_phone: value });
                                                } catch {
                                                    // error handled by mutation
                                                }
                                            }}
                                            disabled={isCompletedJobLocked}
                                        />
                                    </div>
                                )}
                                </div>
                            )}

                            {/* Notes Section */}
                            <div
                                ref={notesCardRef}
                                className={`bg-white p-3 rounded-xl border shadow-sm transition-all 
                                    ${editingCard === 'notes' ? 'border-blue-400 ring-2 ring-blue-100' :
                                        editingCard === 'internal_notes' ? 'border-amber-400 ring-2 ring-amber-100' :
                                            'border-gray-200'} 
                                    ${(canEditField('notes') || (!isTechnician && canEditField('internal_notes'))) ? 'cursor-pointer hover:border-gray-300' : ''}`}
                                onBlurCapture={editingCard === 'notes' || editingCard === 'internal_notes' ? handleCardBlur(editingCard as Exclude<EditableCard, null>) : undefined}
                            >
                                {/* Notes Part */}
                                <div
                                    onClick={(e) => {
                                        if (!canEditField('notes')) return;
                                        if (editingCard === 'notes') return;
                                        if (isAnyEditActive()) {
                                            cancelAllEdits();
                                            return;
                                        }
                                        e.stopPropagation();
                                        setEditingCard('notes');
                                    }}
                                >
                                    <div className="flex items-center gap-2 mb-2">
                                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Notes</h3>
                                        {!job.notes && canEditField('notes') && <Edit2 className="h-3.5 w-3.5 text-gray-400" />}
                                    </div>
                                    {editingCard === 'notes' ? (
                                        <textarea
                                            ref={notesRef}
                                            autoFocus
                                            value={formData.notes ?? job.notes ?? ''}
                                            onChange={(e) => handleInputChange('notes', e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            onFocus={(e) => {
                                            fitTextareaToText(e.target);
                                                // Move cursor to end of text
                                                const len = e.target.value.length;
                                                e.target.setSelectionRange(len, len);
                                            }}
                                            onInput={(e) => fitTextareaToText(e.currentTarget)}
                                            className="w-full text-sm border-gray-300 rounded-lg p-2 resize-none overflow-hidden"
                                            rows={3}
                                        />
                                    ) : null}
                                    {editingCard === 'notes' && renderTextEditActions()}
                                    {editingCard === 'notes' ? null : job.notes ? (
                                        (() => {
                                            const isLong = typeof job.notes === 'string' && (job.notes.length > 100 || (job.notes.match(/\n/g) || '').length > 2);

                                            return (
                                                <div className="relative">
                                                    <div className="flex items-start gap-2">
                                                        <p className={`flex-1 text-sm text-gray-800 break-words whitespace-pre-line break-word leading-relaxed ${!showFullNotes && isLong ? 'line-clamp-2' : ''}`}>
                                                            {job.notes}
                                                        </p>
                                                        {canEditField('notes') && <Edit2 className="h-3.5 w-3.5 text-gray-400 shrink-0 mt-0.5" />}
                                                    </div>
                                                    {isLong && (
                                                        <div className="mt-2">
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setShowFullNotes(s => !s);
                                                                }}
                                                                className="text-xs text-blue-600 font-medium"
                                                            >
                                                                {showFullNotes ? 'See less' : 'See more'}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })()
                                    ) : null}
                                </div>

                                {/* Internal Notes Part */}
                                {!isTechnician && (
                                    <>
                                        <div className="my-3 border-t border-gray-100" />
                                        <div
                                            onClick={(e) => {
                                                if (!canEditField('internal_notes')) return;
                                                if (editingCard === 'internal_notes') return;
                                                if (isAnyEditActive()) {
                                                    cancelAllEdits();
                                                    return;
                                                }
                                                e.stopPropagation();
                                                setFormData(prev => ({ ...prev, internal_notes: job.internal_notes || '' }));
                                                setEditingCard('internal_notes');
                                            }}
                                        >
                                            <div className="flex items-center gap-2 mb-2">
                                                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Internal Notes</h3>
                                                {!job.internal_notes && canEditField('internal_notes') && <Edit2 className="h-3.5 w-3.5 text-gray-400" />}
                                            </div>
                                            {editingCard === 'internal_notes' ? (
                                                <textarea
                                                    autoFocus
                                                    value={formData.internal_notes ?? job.internal_notes ?? ''}
                                                    onChange={(e) => handleInputChange('internal_notes', e.target.value)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onFocus={(e) => {
                                            fitTextareaToText(e.target);
                                                        const len = e.target.value.length;
                                                        e.target.setSelectionRange(len, len);
                                                    }}
                                                    onInput={(e) => fitTextareaToText(e.currentTarget)}
                                            className="w-full text-sm border-gray-300 rounded-lg p-2 resize-none overflow-hidden"
                                                    rows={3}
                                                    placeholder="Add internal notes (admin only)..."
                                                />
                                            ) : null}
                                            {editingCard === 'internal_notes' && renderTextEditActions()}
                                            {editingCard === 'internal_notes' ? null : job.internal_notes ? (
                                                <div className="flex items-start gap-2">
                                                    <p className="flex-1 text-sm text-gray-800 break-words whitespace-pre-line leading-relaxed">
                                                        {job.internal_notes}
                                                    </p>
                                                    {canEditField('internal_notes') && <Edit2 className="h-3.5 w-3.5 text-gray-400 shrink-0 mt-0.5" />}
                                                </div>
                                            ) : null}
                                        </div>
                                    </>
                                )}


                            </div>

                            {/* Tags Section */}
                            {job.company?.id && (
                                <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Tags</h3>
                                    <TagSelector
                                        companyId={job.company.id}
                                        selectedTags={job.tags || []}
                                        onChange={(tags) => setJobTagsMutation.mutate(tags)}
                                        allowCreate={true}
                                        disabled={!canEditField('tags')}
                                    />
                                </div>
                            )}



                            {/* Extensible Fields & Other Content - Collapsible */}
                            {mergedExtraFields.length > 0 && (
                                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                    {/* Collapsible Header */}
                                    <button
                                        type="button"
                                        onClick={() => setIsAdditionalFieldsExpanded(!isAdditionalFieldsExpanded)}
                                        className="w-full p-3 flex items-center justify-between bg-gray-50/50 hover:bg-gray-100/50 transition-colors"
                                    >
                                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Additional Fields</h3>
                                        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isAdditionalFieldsExpanded ? 'rotate-180' : ''}`} />
                                    </button>

                                    {/* Collapsible Content */}
                                    {isAdditionalFieldsExpanded && (
                                        <div className="p-3 pt-0 space-y-2 border-t border-gray-100 animate-in slide-in-from-top-1">
                                            {mergedExtraFields.map((mergedField) => {
                                                const fieldValue = getFieldValue(job.extra_fields, mergedField.key);
                                                const formattedValue = formatFieldValue(fieldValue, mergedField.type);
                                                const isEditing = editingExtraField === mergedField.key;

                                                // Handler to save the extra field
                                                const handleSaveExtraField = async () => {
                                                    if (!job) return;
                                                    setEditingExtraField(null);

                                                    let parsedValue: any = extraFieldInputValue;
                                                    // Parse value based on field type
                                                    if (mergedField.type === 'Boolean') {
                                                        parsedValue = extraFieldInputValue === 'true';
                                                    }

                                                    // Only save if value changed
                                                    if (parsedValue !== fieldValue) {
                                                        try {
                                                            const updatedExtraFields = {
                                                                ...(job.extra_fields || {}),
                                                                [mergedField.key]: parsedValue
                                                            };
                                                            await updateJobMutation.mutateAsync({ extra_fields: updatedExtraFields });
                                                        } catch (error) {
                                                            clientLogger.error('Failed to update extra field:', error);
                                                        }
                                                    }
                                                };

                                                return (
                                                    <div key={mergedField.key} className="flex justify-between items-center gap-2 text-sm border-b border-gray-50 last:border-0 pb-1">
                                                        <span className="text-gray-600 shrink-0">{mergedField.key}</span>
                                                        {isEditing ? (
                                                            // Editor takes the rest of the row: a fixed w-24 box was unusable on a phone.
                                                            <div className="flex flex-1 min-w-0 items-center gap-1">
                                                                {mergedField.type === 'Boolean' ? (
                                                                    <select
                                                                        value={extraFieldInputValue}
                                                                        onChange={(e) => setExtraFieldInputValue(e.target.value)}
                                                                        onBlur={handleSaveExtraField}
                                                                        autoFocus
                                                                        className="w-full px-2 py-2 text-base border border-primary-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                                                                    >
                                                                        <option value="true">Yes</option>
                                                                        <option value="false">No</option>
                                                                    </select>
                                                                ) : (
                                                                    <input
                                                                        type="text"
                                                                        inputMode="text"
                                                                        value={extraFieldInputValue}
                                                                        onChange={(e) => setExtraFieldInputValue(e.target.value)}
                                                                        onBlur={handleSaveExtraField}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === 'Enter') {
                                                                                e.preventDefault();
                                                                                handleSaveExtraField();
                                                                            } else if (e.key === 'Escape') {
                                                                                setEditingExtraField(null);
                                                                            }
                                                                        }}
                                                                        autoFocus
                                                                        // text-base, not text-sm: iOS zooms the page on focus for inputs under 16px.
                                                                        className="w-full px-2 py-2 text-base border border-primary-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 text-right"
                                                                    />
                                                                )}
                                                            </div>
                                                        ) : canEditJobs ? (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    // Convert value to string for input
                                                                    let inputVal = '';
                                                                    if (mergedField.type === 'Boolean') {
                                                                        inputVal = fieldValue ? 'true' : 'false';
                                                                    } else if (fieldValue !== null && fieldValue !== undefined) {
                                                                        inputVal = String(fieldValue);
                                                                    }
                                                                    setExtraFieldInputValue(inputVal);
                                                                    setEditingExtraField(mergedField.key);
                                                                }}
                                                                className="inline-flex items-center gap-1 text-gray-900 font-medium hover:text-primary-600 hover:bg-primary-50 px-1.5 py-0.5 -mx-1.5 rounded transition-colors"
                                                                title={`Click to edit ${mergedField.key}`}
                                                            >
                                                                {formattedValue}
                                                                <Edit2 className="h-3 w-3 text-gray-400" />
                                                            </button>
                                                        ) : (
                                                            <span className="text-gray-900 font-medium">{formattedValue}</span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Divider above financial cards */}
                            <div className="flex items-center gap-3 my-2">
                                <div className="flex-1 h-0.5 bg-gray-400" />
                                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Financial</span>
                                <div className="flex-1 h-0.5 bg-gray-400" />
                            </div>

                            {/* Job Items Section */}
                            <JobItemsSection
                                items={job.items || []}
                                companyId={job.company?.id}
                                taxRate={companyInvoiceSettings?.tax_rate || 0}
                                defaultItemTaxable={companyInvoiceSettings?.default_item_taxable ?? true}
                                onItemsChange={handleJobItemsChange}
                                isEditable={canEditField('total') && !isEditMode && !editingCard}
                                collapsible
                                defaultCollapsed
                                estimate={job.estimate}
                                onEstimateChange={canEditField('total') ? async (estimate) => {
                                    await updateJobMutation.mutateAsync({ estimate });
                                } : undefined}
                            />

                            {/* Collapsible Payments Section */}
                            {(() => {
                                const totalPaid = job.payments?.reduce((sum, p) => sum + p.amount, 0) || 0;
                                const jobTotal = job.total || 0;
                                const balance = jobTotal - totalPaid;

                                return (
                                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                        {/* Header */}
                                        <button
                                            type="button"
                                            className="w-full px-2.5 py-2.5 flex items-center justify-between gap-2 bg-gray-50/50 hover:bg-gray-100/50 transition-colors"
                                            onClick={() => setShowPaymentsExpanded(!showPaymentsExpanded)}
                                        >
                                            <CollapsibleSummaryHeader
                                                leading={
                                                    <>
                                                        <div className="flex items-center gap-1.5 shrink-0">
                                                            <CreditCard className="h-4 w-4 text-green-600 shrink-0" />
                                                            <span className="font-semibold text-gray-900">Payments</span>
                                                        </div>
                                                        <div className="w-px h-4 bg-gray-300 shrink-0" aria-hidden />
                                                        <span className={`font-bold text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${getPaymentStatusColor(job.payment_status)}`}>
                                                            {job.payment_status?.replace(/([A-Z])/g, ' $1').trim() || 'Not Paid'}
                                                        </span>
                                                    </>
                                                }
                                                trailing={
                                                    <>
                                                        <span className="text-gray-600 font-semibold whitespace-nowrap">Paid: <span className={`font-bold ${totalPaid > 0 ? 'text-green-600' : 'text-gray-400'}`}>${totalPaid.toFixed(2)}</span></span>
                                                        <div className="w-px h-4 bg-gray-300 shrink-0" aria-hidden />
                                                        <span className="text-gray-600 font-semibold whitespace-nowrap">Balance: <span className={`font-bold ${balance > 0 ? 'text-orange-600' : 'text-green-600'}`}>${balance.toFixed(2)}</span></span>
                                                    </>
                                                }
                                            />
                                            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform shrink-0 ${showPaymentsExpanded ? 'rotate-180' : ''}`} />
                                        </button>

                                        {/* Expanded Content - PaymentCard */}
                                        {showPaymentsExpanded && (
                                            <div className="border-t border-gray-100 animate-in slide-in-from-top-1">
                                                <PaymentCard job={job} onJobUpdated={onJobUpdated} compact disabled={!canEditField('payments')} />
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}

                            {/* Job Expenses - Collapsible */}
                            {(() => {
                                const companyParts = job.expenses?.filter(e => e.type === 'CompanyParts').reduce((sum, e) => sum + e.amount, 0) || 0;
                                const techParts = job.expenses?.filter(e => e.type === 'TechnicianParts').reduce((sum, e) => sum + e.amount, 0) || 0;
                                const totalExpenses = companyParts + techParts;

                                return (
                                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                        {/* Collapsed Header */}
                                        <button
                                            type="button"
                                            onClick={() => setShowExpensesExpanded(!showExpensesExpanded)}
                                            className="w-full px-2.5 py-2.5 flex items-center justify-between gap-2 bg-gray-50/50 hover:bg-gray-100/50 transition-colors"
                                        >
                                            <CollapsibleSummaryHeader
                                                leading={
                                                    <div className="flex items-center gap-1.5 shrink-0">
                                                        <Receipt className="h-4 w-4 text-orange-500 shrink-0" />
                                                        <span className="font-semibold text-gray-900">Expenses</span>
                                                    </div>
                                                }
                                                trailing={
                                                    <>
                                                        <span className={`font-semibold whitespace-nowrap ${totalExpenses > 0 ? 'text-orange-600' : 'text-gray-900'}`}>${totalExpenses.toFixed(2)}</span>
                                                        {companyParts > 0 && (
                                                            <>
                                                                <div className="w-px h-4 bg-gray-300 shrink-0" aria-hidden />
                                                                <span className="text-gray-600 font-semibold whitespace-nowrap">Co. Parts: <span className="font-bold text-gray-900">${companyParts.toFixed(2)}</span></span>
                                                            </>
                                                        )}
                                                        {techParts > 0 && (
                                                            <>
                                                                <div className="w-px h-4 bg-gray-300 shrink-0" aria-hidden />
                                                                <span className="text-gray-600 font-semibold whitespace-nowrap">Tech Parts: <span className="font-bold text-gray-900">${techParts.toFixed(2)}</span></span>
                                                            </>
                                                        )}
                                                    </>
                                                }
                                            />
                                            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform shrink-0 ${showExpensesExpanded ? 'rotate-180' : ''}`} />
                                        </button>

                                        {/* Expanded Content - JobExpensesCard */}
                                        {showExpensesExpanded && (
                                            <div className="border-t border-gray-100 animate-in slide-in-from-top-1">
                                                <JobExpensesCard job={job} onJobUpdated={onJobUpdated} compact disabled={!canEditField('expenses')} />
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}

                            {/* Payout Information Section */}
                            <TechnicianPaySection job={job} onJobUpdated={onJobUpdated} compact />

                            {/* Origin Job */}
                            {job.origin_job && (
                                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                                    <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
                                        <Link className="h-4 w-4 text-indigo-500" />
                                        <h3 className="text-sm font-semibold text-gray-900">Origin Job</h3>
                                    </div>
                                    <div className="divide-y divide-gray-100">
                                        <button
                                            onClick={() => setViewOriginJobId(job.origin_job!.id)}
                                            className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
                                        >
                                            <div className="flex items-center gap-3">
                                                <span className="text-sm font-medium text-gray-900">#{job.origin_job.invoice_number}</span>
                                                {job.origin_job.date_executed_start && (
                                                    <span className="text-xs text-gray-500">
                                                        {formatDateOnlyInCompanyTimeZone(job.origin_job.date_executed_start, companyTimeZone)}
                                                    </span>
                                                )}
                                            </div>
                                            {job.origin_job.status && (
                                                <span
                                                    className="text-xs font-medium px-2 py-0.5 rounded-full"
                                                    style={{
                                                        backgroundColor: job.origin_job.status_color || '#e2e8f0',
                                                        color: (() => {
                                                            const hex = (job.origin_job.status_color || '#e2e8f0').replace('#', '');
                                                            const r = parseInt(hex.substr(0, 2), 16);
                                                            const g = parseInt(hex.substr(2, 2), 16);
                                                            const b = parseInt(hex.substr(4, 2), 16);
                                                            return ((r * 299 + g * 587 + b * 114) / 1000) > 128 ? '#0f172a' : '#fff';
                                                        })()
                                                    }}
                                                >
                                                    {job.origin_job.status}
                                                </span>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Follow-up Visits */}
                            {job.follow_up_jobs && job.follow_up_jobs.length > 0 && (
                                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                                    <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
                                        <CalendarPlus className="h-4 w-4 text-indigo-500" />
                                        <h3 className="text-sm font-semibold text-gray-900">Follow-up Visits</h3>
                                    </div>
                                    <div className="divide-y divide-gray-100">
                                        {job.follow_up_jobs.map((fj) => (
                                            <button
                                                key={fj.id}
                                                onClick={() => setViewFollowUpJobId(fj.id)}
                                                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <span className="text-sm font-medium text-gray-900">#{fj.invoice_number}</span>
                                                    {fj.date_executed_start && (
                                                        <span className="text-xs text-gray-500">
                                                            {formatDateOnlyInCompanyTimeZone(fj.date_executed_start, companyTimeZone)}
                                                        </span>
                                                    )}
                                                </div>
                                                {fj.status && (
                                                    <span
                                                        className="text-xs font-medium px-2 py-0.5 rounded-full"
                                                        style={{
                                                            backgroundColor: fj.status_color || '#e2e8f0',
                                                            color: (() => {
                                                                const hex = (fj.status_color || '#e2e8f0').replace('#', '');
                                                                const r = parseInt(hex.substr(0, 2), 16);
                                                                const g = parseInt(hex.substr(2, 2), 16);
                                                                const b = parseInt(hex.substr(4, 2), 16);
                                                                return ((r * 299 + g * 587 + b * 114) / 1000) > 128 ? '#0f172a' : '#fff';
                                                            })()
                                                        }}
                                                    >
                                                        {fj.status}
                                                    </span>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Estimates created from this job */}
                            <LinkedEstimates
                                filter={{ source_job_id: job.id }}
                                companyId={job.company?.id}
                                onOpen={openLinkedEstimate}
                                variant="mobile"
                            />

                            {/* Collapsible Meta Info (Audit Log) */}
                            <div className="border-t border-gray-200 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowMetaInfo(!showMetaInfo)}
                                    className="flex items-center gap-2 text-xs font-medium text-gray-500 hover:text-gray-700 w-full justify-center"
                                >
                                    {showMetaInfo ? 'Hide Job Meta' : 'Show Job Meta'}
                                    <Clock className="h-3 w-3" />
                                </button>

                                {showMetaInfo && (
                                    <div className="mt-3 space-y-2 text-xs text-gray-500 bg-gray-50 p-3 rounded-lg animate-in fade-in">
                                        <div className="flex justify-between">
                                            <span>Created By:</span>
                                            <span className="text-gray-700">{job.created_by?.name || 'Unknown'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Source:</span>
                                            <span className="text-gray-700">{job.source?.name || 'N/A'}</span>
                                        </div>
                                        {canViewPayoutFields && job.source_commission !== undefined && job.source_commission !== null && job.source_commission > 0 && (
                                            <div className="flex justify-between">
                                                <span>Source Cost:</span>
                                                <span className="text-gray-700 font-medium">${job.source_commission.toFixed(2)}</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between">
                                            <span>Created At:</span>
                                            <span className="text-gray-700">{formatInstantInCompanyTimeZone(job.date_created, companyTimeZone)}</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                        </div>
                    </form>
                </div>

                {/* Sticky Footer Action Bar */}
                {/* Hidden, not unmounted, while the keyboard is open: TakePaymentButton below
                    owns the payment modal's open state, so unmounting this bar tore the modal
                    down the moment its amount field took focus and raised the keyboard. */}
                {!isEditMode && (
                    <div className={`absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 safe-area-bottom-padded z-20 touch-none ${isKeyboardOpen ? 'hidden' : ''}`}>
                        <div className="grid grid-cols-5 gap-1 px-2 pt-2">
                            <button
                                onClick={() => setShowVisitsModal(true)}
                                className="flex flex-col items-center justify-center gap-1 p-1 rounded-lg hover:bg-gray-50 text-gray-600 active:bg-gray-100 transition-colors"
                            >
                                <div className="relative flex items-center justify-center w-10 h-10 rounded-full border border-gray-300 bg-white shadow-sm">
                                    <Clock className="h-5 w-5 text-gray-700" />
                                    {job.active_visit && (
                                        <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-green-500 ring-2 ring-white animate-pulse" />
                                    )}
                                </div>
                                <span className="text-[10px] font-medium text-gray-600">Time Log</span>
                            </button>

                            {job.company?.id ? (
                                <TakePaymentButton
                                    job={job}
                                    disabled={job.payment_status === 'PaidFully' || !job.total}
                                    onPaymentComplete={(updatedJob) => {
                                        if (updatedJob) {
                                            // Use the updated job from the PATCH response directly
                                            if (onJobUpdated) onJobUpdated(updatedJob);
                                        } else {
                                            // Stripe path: no updatedJob returned, so refetch
                                            queryClient.invalidateQueries({ queryKey: ['jobs'] });
                                            if (onJobUpdated) {
                                                apiService.getJobById(job.id).then(onJobUpdated);
                                            }
                                        }
                                    }}
                                    renderCustom={(onClick: () => void, isDisabled: boolean) => (
                                        <button
                                            onClick={onClick}
                                            disabled={isDisabled}
                                            className={`flex flex-col items-center justify-center gap-1 p-1 rounded-lg hover:bg-gray-50 text-gray-600 active:bg-gray-100 transition-colors ${isDisabled ? 'opacity-50' : ''}`}
                                        >
                                            <div className={`flex items-center justify-center w-10 h-10 rounded-full border shadow-sm ${isDisabled ? 'border-gray-200 bg-gray-50' : 'border-green-300 bg-green-50'}`}>
                                                <DollarSign className={`h-5 w-5 ${isDisabled ? 'text-gray-400' : 'text-green-600'}`} />
                                            </div>
                                            <span className="text-[10px] font-medium text-gray-600">Payment</span>
                                        </button>
                                    )}
                                />
                            ) : (
                                <button
                                    disabled
                                    className="flex flex-col items-center justify-center gap-1 p-1 rounded-lg text-gray-600 opacity-50"
                                >
                                    <div className="flex items-center justify-center w-10 h-10 rounded-full border border-gray-200 bg-gray-50 shadow-sm">
                                        <DollarSign className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <span className="text-[10px] font-medium text-gray-600">Payment</span>
                                </button>
                            )}

                            <button
                                onClick={() => {
                                    // If we came from InvoiceDetailsModal, just close this modal (go back) instead of opening another
                                    if (openedFromInvoice) {
                                        onClose();
                                        return;
                                    }
                                    setShowInvoiceModal(true);
                                }}
                                disabled={!job?.invoice_number}
                                className="flex flex-col items-center justify-center gap-1 p-1 rounded-lg hover:bg-gray-50 text-gray-600 active:bg-gray-100 transition-colors disabled:opacity-50"
                            >
                                <div className="flex items-center justify-center w-10 h-10 rounded-full border border-gray-300 bg-white shadow-sm">
                                    <CreditCard className="h-5 w-5 text-gray-700" />
                                </div>
                                <span className="text-[10px] font-medium text-gray-600">Invoice</span>
                            </button>

                            <button
                                onClick={() => setShowImagesModal(true)}
                                className="flex flex-col items-center justify-center gap-1 p-1 rounded-lg hover:bg-gray-50 text-gray-600 active:bg-gray-100 transition-colors"
                                title={attachmentCount > 0 ? `${attachmentCount} attachment${attachmentCount === 1 ? '' : 's'}` : undefined}
                            >
                                <div className="relative flex items-center justify-center w-10 h-10 rounded-full border border-gray-300 bg-white shadow-sm">
                                    <Paperclip className="h-5 w-5 text-gray-700" />
                                    {attachmentCount > 0 && (
                                        <span
                                            className="absolute -top-0.5 -right-0.5 min-h-[1.125rem] min-w-[1.125rem] px-1 flex items-center justify-center rounded-full bg-blue-600 text-[10px] font-semibold text-white leading-none shadow-sm ring-2 ring-white"
                                            aria-hidden
                                        >
                                            {attachmentCount > 99 ? '99+' : attachmentCount}
                                        </span>
                                    )}
                                </div>
                                <span className="text-[10px] font-medium text-gray-600">Attach</span>
                            </button>

                            <div className="relative" ref={moreMenuRef}>
                                <button
                                    onClick={() => setShowMoreMenu(!showMoreMenu)}
                                    className="flex flex-col items-center justify-center gap-1 p-1 rounded-lg hover:bg-gray-50 text-gray-600 active:bg-gray-100 transition-colors"
                                >
                                    <div className="flex items-center justify-center w-10 h-10 rounded-full border border-gray-300 bg-white shadow-sm">
                                        <MoreVertical className="h-5 w-5 text-gray-700" />
                                    </div>
                                    <span className="text-[10px] font-medium text-gray-600">More</span>
                                </button>

                                {/* More Menu Dropdown */}
                                {showMoreMenu && (
                                    <div className={`absolute bottom-full right-0 mb-2 ${showSmsSubmenu ? 'w-64' : 'w-48'} bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50 animate-in fade-in slide-in-from-bottom-2`}>
                                        {showSmsSubmenu ? (
                                            <SmsTemplateMenu
                                                job={job}
                                                customerName={primaryContact?.name || ''}
                                                onBack={() => setShowSmsSubmenu(false)}
                                                onPickCustom={() => {
                                                    setShowMoreMenu(false);
                                                    if (primaryContact?.phone) {
                                                        setSmsSelectorState({
                                                            isOpen: true,
                                                            recipientPhone: primaryContact.phone,
                                                            recipientName: primaryContact.name || 'Client',
                                                            recipientType: 'Client',
                                                            jobId: job.id,
                                                            masked: primaryContact.phone_masked,
                                                            contactId: primaryContact.id,
                                                            contactWhich: 'primary',
                                                        });
                                                    }
                                                }}
                                                onPickTemplate={(message, warning) => {
                                                    setShowMoreMenu(false);
                                                    setTemplateSmsState({
                                                        message,
                                                        warning,
                                                        recipientPhone: primaryContact?.phone ?? '',
                                                        recipientName: primaryContact?.name || 'Client',
                                                        toContactRef: primaryContact?.phone_masked
                                                            ? `contact:${primaryContact.id}:primary`
                                                            : undefined,
                                                    });
                                                }}
                                            />
                                        ) : (
                                        <>
                                        {onCancelJob && !job.canceled_at && (
                                            <button
                                                onClick={() => {
                                                    setShowMoreMenu(false);
                                                    setShowConfirmCancel(true);
                                                }}
                                                className="w-full px-4 py-2.5 text-left text-sm text-orange-600 hover:bg-gray-50 transition-colors flex items-center gap-2"
                                            >
                                                <X className="h-4 w-4" />
                                                Cancel Job
                                            </button>
                                        )}
                                        {onUncancelJob && job.canceled_at && (
                                            <button
                                                onClick={() => {
                                                    setShowMoreMenu(false);
                                                    setShowConfirmUncancel(true);
                                                }}
                                                className="w-full px-4 py-2.5 text-left text-sm text-teal-600 hover:bg-gray-50 transition-colors flex items-center gap-2"
                                            >
                                                <X className="h-4 w-4" />
                                                Un-cancel Job
                                            </button>
                                        )}
                                        {onDelete && (
                                            <button
                                                onClick={() => {
                                                    setShowMoreMenu(false);
                                                    setShowConfirmDelete(true);
                                                }}
                                                className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-gray-50 transition-colors flex items-center gap-2"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                                Delete Job
                                            </button>
                                        )}
                                        <button
                                            onClick={() => {
                                                setShowMoreMenu(false);
                                                setShowShareModal(true);
                                            }}
                                            className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                                        >
                                            <Share className="h-4 w-4" />
                                            Share Job
                                        </button>
                                        {companyInvoiceSettings?.review_link && (
                                            <button
                                                onClick={() => {
                                                    setShowMoreMenu(false);
                                                    setShowReviewQr(true);
                                                }}
                                                className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                                            >
                                                <QrCode className="h-4 w-4" />
                                                Review QR Code
                                            </button>
                                        )}
                                        <button
                                            onClick={() => {
                                                setShowMoreMenu(false);
                                                setShowConvertConfirm(true);
                                            }}
                                            className="w-full px-4 py-2.5 text-left text-sm text-amber-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                                        >
                                            <FileText className="h-4 w-4" />
                                            Create Estimate
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowMoreMenu(false);
                                                setIsScheduleVisitOpen(true);
                                            }}
                                            className="w-full px-4 py-2.5 text-left text-sm text-indigo-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                                        >
                                            <CalendarPlus className="h-4 w-4" />
                                            Schedule New Visit
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowMoreMenu(false);
                                                setShowExpenseAttachmentsModal(true);
                                            }}
                                            className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                                        >
                                            <Paperclip className="h-4 w-4" />
                                            Expense Receipts
                                            {expenseAttachmentCount > 0 && (
                                                <span className="ml-auto bg-blue-100 text-blue-700 text-xs font-semibold px-1.5 py-0.5 rounded-full">
                                                    {expenseAttachmentCount}
                                                </span>
                                            )}
                                        </button>
                                        {primaryContact?.phone && (
                                            <button
                                                onClick={() => setShowSmsSubmenu(true)}
                                                className="w-full px-4 py-2.5 text-left text-sm text-blue-600 hover:bg-gray-50 transition-colors flex items-center gap-2"
                                            >
                                                <MessageSquare className="h-4 w-4" />
                                                <span className="flex-1">SMS Client</span>
                                                <ChevronRight className="h-4 w-4" />
                                            </button>
                                        )}
                                        {isAdmin && (
                                            <button
                                                onClick={() => {
                                                    setShowMoreMenu(false);
                                                    setShowCallsModal(true);
                                                }}
                                                className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                                            >
                                                <Phone className="h-4 w-4" />
                                                Calls
                                            </button>
                                        )}
                                        </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

            </div>

            {/* Confirm Delete Dialog */}
            {onDelete && (
                <ConfirmDialog
                    isOpen={showConfirmDelete}
                    onClose={() => setShowConfirmDelete(false)}
                    onConfirm={async () => {
                        await onDelete!(job);
                    }}
                    title="Delete Job"
                    message={
                        <div className="space-y-2">
                            <span>Are you sure you want to delete this job?</span>
                            <span className="block text-sm">{destructiveCopy.cannotUndo}</span>
                            <span className="block text-sm text-amber-800">{destructiveCopy.jobDeleteCascade}</span>
                        </div>
                    }
                    confirmText="Delete"
                    cancelText="Cancel"
                />
            )}

            {/* Invoice Details Modal */}
            <InvoiceDetailsModal
                isOpen={showInvoiceModal}
                onClose={() => setShowInvoiceModal(false)}
                jobId={job.id}
                companyId={job.company?.id}
                onInvoiceUpdated={() => {
                    queryClient.invalidateQueries({ queryKey: ['invoice', job.id] });
                    queryClient.invalidateQueries({ queryKey: ['jobs'] });
                    if (onJobUpdated) {
                        apiService.getJobById(job.id).then(onJobUpdated);
                    }
                }}
                openedFromJob={true}
            />
            <JobAttachmentsModal
                isOpen={showImagesModal}
                onClose={() => setShowImagesModal(false)}
                jobId={job.id}
                onAttachmentsUpdated={handleImagesUpdated}
            />
            <JobExpenseAttachmentsModal
                isOpen={showExpenseAttachmentsModal}
                onClose={() => setShowExpenseAttachmentsModal(false)}
                jobId={job.id}
                onAttachmentsUpdated={() => {
                    queryClient.invalidateQueries({ queryKey: ['jobExpenseAttachments', job?.id] });
                }}
            />
            <JobContactsMobileModal
                isOpen={showContactsModal}
                onClose={() => setShowContactsModal(false)}
                job={job}
                onJobUpdated={onJobUpdated}
                onPastJobSelect={(jobId) => {
                    setShowContactsModal(false);
                    setPastJobId(jobId);
                }}
                onLeaveJob={onClose}
            />

            {/* Past Job Modal */}
            {pastJobId && (
                <JobDetailsModal
                    isOpen={!!pastJobId}
                    onClose={() => setPastJobId(null)}
                    jobId={pastJobId}
                    onCancelJob={onCancelJob}
                    onJobUpdated={() => {
                        queryClient.invalidateQueries({ queryKey: ['jobs'] });
                    }}
                    openedFromPastJobs={true}
                />
            )}

            {/* Job Visits / Time Log Modal */}
            <JobVisitsModal
                isOpen={showVisitsModal}
                onClose={() => setShowVisitsModal(false)}
                job={job}
                onJobUpdated={onJobUpdated}
            />

            {/* Schedule Another Visit Modal */}
            <NewJobModal
                isOpen={isScheduleVisitOpen}
                onClose={() => setIsScheduleVisitOpen(false)}
                onJobCreated={() => {
                    setIsScheduleVisitOpen(false);
                    queryClient.invalidateQueries({ queryKey: ['job', job.id] });
                    queryClient.invalidateQueries({ queryKey: ['jobs'] });
                }}
                initialValues={{
                    address: job.address,
                    unit_name: job.unit_name,
                    company_id: job.company?.id,
                    assigned_technician_id: job.assigned_technician?.id,
                    job_type_id: job.job_type?.id,
                    notes: `Additional visit to job #${job.invoice_number}`,
                    origin_job_id: job.id,
                    contacts: (job.contacts ?? []).map(c => c.contact as ContactDTO),
                }}
            />

            {/* Origin Job Modal */}
            {viewOriginJobId && (
                <JobDetailsModal
                    isOpen={!!viewOriginJobId}
                    onClose={() => setViewOriginJobId(null)}
                    jobId={viewOriginJobId}
                    onCancelJob={onCancelJob}
                    onJobUpdated={() => {
                        queryClient.invalidateQueries({ queryKey: ['jobs'] });
                    }}
                    openedFromPastJobs={true}
                />
            )}

            {/* Follow-up Visit Modal */}
            {viewFollowUpJobId && (
                <JobDetailsModal
                    isOpen={!!viewFollowUpJobId}
                    onClose={() => setViewFollowUpJobId(null)}
                    jobId={viewFollowUpJobId}
                    onCancelJob={onCancelJob}
                    onJobUpdated={() => {
                        queryClient.invalidateQueries({ queryKey: ['jobs'] });
                    }}
                    openedFromPastJobs={true}
                />
            )}

            {showConvertConfirm && job && (
                <CreateEstimateFromJobDialog
                    job={job}
                    onClose={() => setShowConvertConfirm(false)}
                    onDone={handleEstimateCreated}
                />
            )}

            <ConfirmDialog
                isOpen={showConfirmMarkAsap}
                onClose={() => setShowConfirmMarkAsap(false)}
                onConfirm={async () => {
                    // Same as creating an ASAP job: the date becomes a placeholder for "now".
                    const now = new Date().toISOString();
                    setScheduleExpanded(false);
                    setScheduleStart(now);
                    setScheduleEnd(now);
                    await updateJobMutation.mutateAsync({ is_asap: true, date_executed_start: now, date_executed_end: now });
                }}
                title="Mark job ASAP"
                message="The scheduled time will be removed and the job will be marked ASAP. Set a time again whenever one is agreed."
                confirmText="Mark ASAP"
                cancelText="Go Back"
                confirmButtonClassName="bg-red-600 hover:bg-red-700 text-white"
            />

            {/* Item Edit Signature Warning Dialog */}
            <ConfirmDialog
                isOpen={showItemEditSignatureWarning}
                onClose={handleCancelItemEditSignatureWarning}
                onConfirm={handleConfirmItemEditSignatureWarning}
                title="Editing May Void Signature"
                message={"This job's invoice has been signed by the customer.\nEditing items may change the total, which will void their signature.\n\nAre you sure you want to continue?\n\nThe old signed invoice will be kept as a PDF attachment for reference."}
                confirmText="Continue"
                cancelText="Cancel"
                confirmButtonClassName="bg-red-600 hover:bg-red-700 text-white"
            />

            {/* Cancel Job Confirmation Dialog */}
            {showConfirmCancel && onCancelJob && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
                    <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
                        <h3 className="text-lg font-semibold mb-2">Cancel Job</h3>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Reason for cancelation (optional)
                            </label>
                            <input
                                type="text"
                                value={cancelReason}
                                onChange={(e) => setCancelReason(e.target.value)}
                                placeholder="Enter cancellation reason..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            />
                        </div>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowConfirmCancel(false);
                                    setCancelReason('');
                                }}
                                className="btn-secondary"
                            >
                                Go Back
                            </button>
                            <button
                                onClick={() => {
                                    onCancelJob(job, cancelReason || undefined);
                                    setShowConfirmCancel(false);
                                    setCancelReason('');
                                }}
                                className="bg-orange-600 hover:bg-orange-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                            >
                                Cancel Job
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Un-cancel Job Confirmation Dialog */}
            {showConfirmUncancel && onUncancelJob && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
                    <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
                        <h3 className="text-lg font-semibold mb-2">Un-cancel Job</h3>
                        <p className="text-gray-600 mb-4">Are you sure you want to un-cancel this job?</p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setShowConfirmUncancel(false)}
                                className="btn-secondary"
                            >
                                Go Back
                            </button>
                            <button
                                onClick={() => {
                                    onUncancelJob(job);
                                    setShowConfirmUncancel(false);
                                }}
                                className="bg-teal-600 hover:bg-teal-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                            >
                                Un-cancel Job
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Templated SMS to the customer (locked 10DLC body) */}
            {templateSmsState && (
                <SendSmsModal
                    isOpen
                    onClose={() => setTemplateSmsState(null)}
                    recipientPhone={templateSmsState.recipientPhone}
                    recipientName={templateSmsState.recipientName}
                    recipientType="Client"
                    jobId={job.id}
                    toContactRef={templateSmsState.toContactRef}
                    initialMessage={templateSmsState.message}
                    warning={templateSmsState.warning}
                    lockMessage
                />
            )}

            {/* SMS Method Selector */}
            <SmsMethodSelector
                isOpen={smsSelectorState.isOpen}
                onClose={() => setSmsSelectorState({ ...smsSelectorState, isOpen: false })}
                recipientPhone={smsSelectorState.recipientPhone}
                recipientName={smsSelectorState.recipientName}
                recipientType={smsSelectorState.recipientType}
                jobId={smsSelectorState.jobId}
                masked={smsSelectorState.masked}
                contactId={smsSelectorState.contactId}
                contactWhich={smsSelectorState.contactWhich}
                technicianUserId={smsSelectorState.technicianUserId}
            />

            {/* Share Job Modal */}
            <JobShareModal
                isOpen={showShareModal}
                onClose={() => setShowShareModal(false)}
                job={job}
            />

            {/* Review QR — shown to the customer on site */}
            {companyInvoiceSettings?.review_link && (
                <JobReviewQrModal
                    isOpen={showReviewQr}
                    onClose={() => setShowReviewQr(false)}
                    reviewUrl={companyInvoiceSettings.review_link}
                    companyName={job.company?.name ?? ''}
                />
            )}

            {/* Call Method Sheet */}
            <CallMethodSheet
                isOpen={callSheetState.isOpen}
                onClose={() => setCallSheetState({ ...callSheetState, isOpen: false })}
                toE164={callSheetState.toE164}
                displayName={callSheetState.displayName}
                jobId={callSheetState.jobId}
                masked={callSheetState.masked}
                contactId={callSheetState.contactId}
                contactWhich={callSheetState.contactWhich}
            />

            {/* Calls for this job */}
            {showCallsModal && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/50" onClick={() => setShowCallsModal(false)} />
                    <div className="relative bg-white rounded-xl shadow-2xl flex flex-col w-full max-w-4xl h-[85vh] overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0">
                            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                                <Phone className="h-5 w-5" /> Calls
                            </h2>
                            <button onClick={() => setShowCallsModal(false)} className="p-2 text-gray-500 hover:bg-gray-100 rounded-full">
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="flex-1 min-h-0">
                            <Suspense fallback={null}>
                                <VoipCallHistoryPage jobId={job.id} />
                            </Suspense>
                        </div>
                    </div>
                </div>
            )}
        </div >,
        document.body
    );
}

